/* eslint-disable @typescript-eslint/no-var-requires */
/* eslint-disable guard-for-in */
import React, { createContext, lazy, PureComponent, ReactElement, Suspense, SVGProps } from 'react';
import * as Sentry from '@sentry/react';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { registerHeightObserver, unregisterHeightObserver } from 'element-height-observer';
import { IntlProvider } from 'react-intl';
import deepmerge from 'deepmerge';
import { Styles, Theme } from 'react-select';
import queryString from 'query-string';
import 'webrtc-adapter';
import { CameraProps } from '../Camera/Camera';
import Loading from '../Loading/Loading';
import withAxios from '../../utilities/withAxios';
import { waitForInflightMutations } from '../../utilities/useAxios';
import style from './App.module.scss';
import { Questionnaire } from '../questionnaire/questionnaireHelpers';
import dateToShortDate from '../questionnaire/questionnaireHelpers/dateToShortDate';
import flattenQuestionnaire from '../questionnaire/questionnaireHelpers/flattenQuestionnaire';
import getProgressValue from '../questionnaire/questionnaireHelpers/getProgressValue';
import getQuestionnaireGetters from '../questionnaire/questionnaireHelpers/getQuestionnaireGetters';
import indexFailedFields from '../questionnaire/questionnaireHelpers/indexFailedFields';
import mergeSelectStyles from '../questionnaire/questionnaireHelpers/mergeSelectStyles';
import config from '../../config';
import {
	isDeviceIOS,
	isFirefoxOnAndroid,
	isMobile,
	isMobileApp,
	isRunningInIFrame,
	isValueEmpty,
	sortStages,
	getAxiosErrorDefaultMessage,
	getBoldDeskWidgetScriptSrc
} from '../../utilities/functionHelpers';
import { QuestionnaireProps } from '../questionnaire/questionnaire';
import { AgreementProps } from '../questionnaire/commonPages/Agreement';
import defaultTheme, { KnownThemeImages } from '../defaultTheme/defaultTheme';
import { MediaProps } from '../questionnaire/Media';
import { IntroProps } from '../Intro/Intro';
import { PermissionsProps } from '../Permissions/Permissions';
import { VerificationHandoverProps } from '../VerificationHandover/VerificationHandover';
import { PaymentInfo } from '../ApplicationPayment/ApplicationPayment';
import Language from '../../enums/Language';
import messages from '../../messages/feMessages';
import LanguageComponent from '../Language/Language';
import { AdditionalQuestion } from '../AdditionalQuestions/AdditionalQuestions';
import QuestionStatusEnum from '../../enums/QuestionStatusEnum';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import SupportingDocumentStatusEnum from '../../enums/SupportingDocumentsStatusEnum';
import CorporateVerification from '../../interfaces/CorporateVerification';
import VerificationStatusEnum from '../../enums/VerificationStatusEnum';
import WebViewEventIdEnum from '../../enums/WebViewEventIdEnum';
import FeeStatusEnum from '../../enums/FeeStatusEnum';
import { getSupportedMimeType } from '../../utilities/mediaHelper';

const feLanguages = require('../../../generatedMessages.js');
const questionnaireLanguages = require('../../../generatedQuestionnaireMessages.js');

// Lazy load components
const Entry = lazy(() => import('../Entry/Entry'));
const QR = lazy(() => import('../QR/BankeraQR'));
const QRReader = lazy(() => import('../QRReader/QRReader'));
const InterimQR = lazy(() => import('../QR/BankeraInterimQR'));
const DocumentUpload = lazy(() => import('../DocumentUpload/DocumentUpload'));
const AdditionalQuestions = lazy(() => import('../AdditionalQuestions/AdditionalQuestions'));
const ApplicationPayment = lazy(() => import('../ApplicationPayment/ApplicationPayment'));
const Confirmation = lazy(() => import('../Confirmation/Confirmation'));
const UnderReview = lazy(() => import('../UnderReview/UnderReview'));
const Failure = lazy(() => import('../Failure/Failure'));
const Unavailable = lazy(() => import('../Unavailable/Unavailable'));
const Success = lazy(() => import('../Success/Success'));

export enum Page {
	ENTRY = 'ENTRY',
	BLANK = 'BLANK',
	INTRO = 'INTRO',
	PERMISSIONS = 'PERMISSIONS',
	MEDIA = 'MEDIA',
	LOADING = 'LOADING',
	SUCCESS = 'SUCCESS',
	UNAVAILABLE = 'UNAVAILABLE',
	FAILURE = 'FAILURE',
	UNDER_REVIEW = 'UNDER_REVIEW',
	// eslint-disable-next-line
	QR = 'QR',
	INTERIM_QR = 'INTERIM_QR',
	QR_READER = 'QR_READER',
	QUESTIONNAIRE = 'QUESTIONNAIRE',
	AGREEMENT = 'AGREEMENT',
	DOCUMENT_UPLOAD = 'DOCUMENT_UPLOAD',
	ADDITIONAL_QUESTIONS = 'ADDITIONAL_QUESTIONS',
	VERIFICATION_HANDOVER = 'VERIFICATION_HANDOVER',
	APPLICATION_PAYMENT = 'APPLICATION_PAYMENT',
	CONFIRMATION = 'CONFIRMATION'
}

export enum QuestionnaireKey {
	SPECTROCOIN_LT = 'SPECTROCOIN_LT',
	SPECTROCOIN_EE = 'SPECTROCOIN_EE',
	BANKERA_LOANS = 'BANKERA_LOANS',
	VERIFO = 'VERIFO',
	PERVESK = 'PERVESK',
	PERVESK_CORPORATE = 'PERVESK_CORPORATE',
	PERVESK_MEDIA = 'PERVESK_MEDIA',
	BANKERA_WALLET = 'BANKERA_WALLET',
	BANKERA_KYB_2023 = 'BANKERA_KYB_2023',
	BANKERA_PRIVATE_2023 = 'BANKERA_PRIVATE_2023',
	BANKERA_MEDIA = 'BANKERA_MEDIA',
	SPECTROCOIN_IBAN = 'SPECTROCOIN_IBAN',
	SPECTROCOIN_IBAN_2023 = 'SPECTROCOIN_IBAN_2023',
	PPB = 'PACIFIC_PRIVATE_BANK',
	PPB_KYB = 'PPB_KYB',
	PPB_MEDIA = 'PPB_MEDIA',
	PPB_PRIVATE = 'PPB_PRIVATE',
	SPECTROCOIN_LOANS = 'SPECTROCOIN_LOANS',
	SPECTROCOIN_CORPORATE = 'SPECTROCOIN_CORPORATE',
	CRYPTO_FOREST = 'CRYPTO_FOREST',
	PAXFUL_LOCALBITCOINS = 'PAXFUL_LOCALBITCOINS',
	BLACKRIDGE_CM_CORPORATE = 'BLACKRIDGE_CM_CORPORATE',
	BLACKRIDGE_CM_PRIVATE = 'BLACKRIDGE_CM_PRIVATE',
	SPECTROCOIN_CORPORATE_BVI = 'SPECTROCOIN_CORPORATE_BVI',
	SPECTROCOIN_MEDIA = 'SPECTROCOIN_MEDIA',
	KVIX = 'KVIX',
	FINRAX_BUSINESS = 'FINRAX_BUSINESS',
	FINRAX_KYB_2023 = 'FINRAX_KYB_2023',
	AMNERA_EOOD_PRIVATE = 'AMNERA_EOOD_PRIVATE',
	AMNERA_EOOD_CORPORATE = 'AMNERA_EOOD_CORPORATE',
	PHOENIX_PRIVATE = 'PHOENIX_PRIVATE',
	PHOENIX_CORPORATE = 'PHOENIX_CORPORATE',
	PHOENIX_TIERS = 'PHOENIX_TIERS',
	NEOCARD_PRIVATE = 'NEOCARD_PRIVATE',
	NEOCARD_CORPORATE = 'NEOCARD_CORPORATE',
	NEOCARD_TIERS = 'NEOCARD_TIERS',
	COLUMIS = 'COLUMIS',
	TEROXX = 'TEROXX',
	TEROXX_CORPORATE = 'TEROXX_CORPORATE',
	TEROXX_MEDIA = 'TEROXX_MEDIA',
	DAW_CORPORATE = 'DAW_CORPORATE',
	DAW_MEDIA = 'DAW_MEDIA',
	BENKER = 'BENKER',
	BENKER_CORPORATE = 'BENKER_CORPORATE',
	BENKER_MEDIA = 'BENKER_MEDIA'
}

export enum UserEvent {
	SESSION_STARTED = 'SESSION_STARTED',
	BROWSER_IS_SUPPORTED = 'BROWSER_IS_SUPPORTED',
	USER_HAS_WEBCAM = 'USER_HAS_WEBCAM',
	CAMERA_ACCESS_PROMPTED = 'CAMERA_ACCESS_PROMPTED',
	CAMERA_ACCESS_GRANTED = 'CAMERA_ACCESS_GRANTED',
	CONSENT_PAGE_OPENED = 'CONSENT_PAGE_OPENED',
	CONSENT_FOR_DATA_PROCESSING = 'CONSENT_FOR_DATA_PROCESSING',
	CONSENT_FOR_BIOMETRICS_PROCESSING = 'CONSENT_FOR_BIOMETRICS_PROCESSING',
	CITIZENSHIP_PAGE_OPENED = 'CITIZENSHIP_PAGE_OPENED',
	CITIZENSHIP_CHOSEN = 'CITIZENSHIP_CHOSEN',
	DOCUMENT_SELECT_PAGE_OPENED = 'DOCUMENT_SELECT_PAGE_OPENED',
	DOCUMENT_TYPE_SELECTED = 'DOCUMENT_TYPE_SELECTED',
	FIRST_DOCUMENT_PHOTO_PAGE_OPENED = 'FIRST_DOCUMENT_PHOTO_PAGE_OPENED',
	DOCUMENT_FRONT_SIDE_UPLOADED = 'DOCUMENT_FRONT_SIDE_UPLOADED',
	USER_RETAKES_PHOTO = 'USER_RETAKES_PHOTO',
	SECOND_DOCUMENT_PHOTO_PAGE_OPENED = 'SECOND_DOCUMENT_PHOTO_PAGE_OPENED',
	DOCUMENT_BACK_SIDE_UPLOADED = 'DOCUMENT_BACK_SIDE_UPLOADED',
	SELFIE_PAGE_OPENED = 'SELFIE_PAGE_OPENED',
	SELFIE_UPLOADED = 'SELFIE_UPLOADED',
	CONFIRMATION_PAGE = 'CONFIRMATION_PAGE',
	REDIRECTION = 'REDIRECTION',
	UNSUPPORTED_BROWSER = 'UNSUPPORTED_BROWSER',
	NO_WEBCAM = 'NO_WEBCAM',
	WEBCAM_ACCESS_REJECTED = 'WEBCAM_ACCESS_REJECTED',
	USER_CONTINUES_TO_PROCEED_IN_APP = 'USER_CONTINUES_TO_PROCEED_IN_APP',
	LINK_SENT = 'LINK_SENT',
	QR_CODE_SHOWN = 'QR_CODE_SHOWN',
	SESSION_LINKED = 'SESSION_LINKED',
	AGREE_WITH_GENERAL_SERVICE_AGREEMENT = 'AGREE_WITH_GENERAL_SERVICE_AGREEMENT',
	AGREE_WITH_ELECTRONIC_MONEY_AND_PAYMENT_ACCOUNT_AGREEMENT = 'AGREE_WITH_ELECTRONIC_MONEY_AND_PAYMENT_ACCOUNT_AGREEMENT',
	AGREE_WITH_PRIVACY_POLICY = 'AGREE_WITH_PRIVACY_POLICY',
	PROOF_OF_ADDRESS_UPLOADED = 'PROOF_OF_ADDRESS_UPLOADED',
	CERTIFICATE_OF_INCORPORATION_OR_EXTRACT_FROM_YOUR_LOCAL_REGISTER_OF_COMMERCE_UPLOADED = 'CERTIFICATE_OF_INCORPORATION_OR_EXTRACT_FROM_YOUR_LOCAL_REGISTER_OF_COMMERCE_UPLOADED',
	MEMORANDUM_AND_ARTICLES_OF_ASSOCIATION_UPLOADED = 'MEMORANDUM_AND_ARTICLES_OF_ASSOCIATION_UPLOADED',
	COMPANYS_REGISTER_OF_SHAREHOLDERS_AND_DIRECTORS_UPLOADED = 'COMPANYS_REGISTER_OF_SHAREHOLDERS_AND_DIRECTORS_UPLOADED',
	FINANCIAL_STATEMENTS_UPLOADED = 'FINANCIAL_STATEMENTS_UPLOADED',
	BANK_ACCOUNT_STATEMENTS_UPLOADED = 'BANK_ACCOUNT_STATEMENTS_UPLOADED',
	UTILITY_BILL_UPLOADED = 'UTILITY_BILL_UPLOADED',
	ANY_OTHER_RELEVANT_DOCUMENTS_UPLOADED = 'ANY_OTHER_RELEVANT_DOCUMENTS_UPLOADED',
	SOURCE_OF_WEALTH_UPLOADED = 'SOURCE_OF_WEALTH_UPLOADED',
	PROOF_OF_BANK_ACCOUNT_OPENED_IN_EU_CREDIT_INSTITUTION_UPLOADED = 'PROOF_OF_BANK_ACCOUNT_OPENED_IN_EU_CREDIT_INSTITUTION_UPLOADED',
	PAGE_OF_PASSPORT_WITH_ISSUE_AND_EXPIRY_DATES_UPLOADED = 'PAGE_OF_PASSPORT_WITH_ISSUE_AND_EXPIRY_DATES_UPLOADED',
	STATEMENT_OF_YOUR_CRYPTO_WALLET_UPLOADED = 'STATEMENT_OF_YOUR_CRYPTO_WALLET_UPLOADED',
	PERSONAL_BANK_ACCOUNT_STATEMENT_UPLOADED = 'PERSONAL_BANK_ACCOUNT_STATEMENT_UPLOADED',
	AGREEMENT_WITH_BUSINESS_PARTNERS_UPLOADED = 'AGREEMENT_WITH_BUSINESS_PARTNERS_UPLOADED',
	OTHER_LICENSE_UPLOADED = 'OTHER_LICENSE_UPLOADED',
	CURRENT_FINANCIAL_STATEMENTS_UPLOADED = 'CURRENT_FINANCIAL_STATEMENTS_UPLOADED',
	PROOF_OF_ADDRESS_REPRESENTATIVE_DIRECTOR_UBO_UPLOADED = 'PROOF_OF_ADDRESS_REPRESENTATIVE_DIRECTOR_UBO_UPLOADED',
	CAMERA_DISCONNECTED = 'CAMERA_DISCONNECTED',
	HANDOVER_PAGE_OPENED = 'HANDOVER_PAGE_OPENED',
	USER_IS_REPRESENTATIVE = 'USER_IS_REPRESENTATIVE',
	USER_IS_NOT_REPRESENTATIVE = 'USER_IS_NOT_REPRESENTATIVE',
	CORPORATE_UNDER_REVIEW_PAGE_OPENED = 'CORPORATE_UNDER_REVIEW_PAGE_OPENED',
	APPLICATION_PAYMENT_PAGE_OPENED = 'APPLICATION_PAYMENT_PAGE_OPENED'
}

export enum FileType {
	PHOTO = 'PHOTO',
	VIDEO = 'VIDEO'
}

export enum CaptureObject {
	PASSPORT_COVER = 'PASSPORT_COVER',
	PASSPORT_PAGE = 'PASSPORT_PAGE',
	ID_FRONT = 'ID_FRONT',
	ID_BACK = 'ID_BACK',
	RESIDENCE_PERMIT_FRONT = 'RESIDENCE_PERMIT_FRONT',
	RESIDENCE_PERMIT_BACK = 'RESIDENCE_PERMIT_BACK',
	DRIVERS_LICENCE_FRONT = 'DRIVERS_LICENCE_FRONT',
	DRIVERS_LICENCE_BACK = 'DRIVERS_LICENCE_BACK',
	SELFIE = 'SELFIE'
}

export enum DocumentType {
	PASSPORT = 'PASSPORT',
	ID_CARD = 'ID_CARD',
	RESIDENCE_PERMIT = 'RESIDENCE_PERMIT',
	DRIVERS_LICENCE = 'DRIVERS_LICENCE'
}

export enum VerificationFailureReason {
	AUTHENTICATION = 'AUTHENTICATION',
	FAILED_UPDATE = 'FAILED_UPDATE',
	FAILED_COMPLETE = 'FAILED_COMPLETE',
	FAILED_MEDIA_UPLOAD = 'FAILED_MEDIA_UPLOAD',
	CAMERA_BEING_USED = 'CAMERA_BEING_USED',
	FAILED_DOCUMENT_INFO_UPLOAD = 'FAILED_DOCUMENT_INFO_UPLOAD'
}

export enum ViewTypeEnum {
	VIEW = 'VIEW',
	CORPORATE_COMPANY_VIEW = 'CORPORATE_COMPANY_VIEW'
}

enum QuestionnaireStatusEnum {
	STARTED = 'STARTED',
	SUBMITTED = 'SUBMITTED',
	APPROVED = 'APPROVED',
	REJECTED = 'REJECTED'
}

interface BaseDocumentInformation {
	firstName: string | null;
	middleName: string | null;
	lastName: string | null;
	companyName: string | null;
	personalCode: string | null;
	nationality: string | null;
	issuingCountry: string | null;
	documentNumber: string | null;
	documentType: DocumentType | null;
	citizenship: string | null;
}

interface DocumentInformation extends BaseDocumentInformation {
	dateOfBirth: Date | null;
	issueDate: Date | null;
	expirationDate: Date | null;
}

interface FetchedDocumentInformation extends BaseDocumentInformation {
	dateOfBirth: string | null;
	issueDate: string | null;
	expirationDate: string | null;
}

interface ContactInformation {
	email: string | null;
	phoneNumber: string | null;
}

export type Information = DocumentInformation & ContactInformation;

interface BaseVerification extends ContactInformation {
	referenceId: string | null;
	questionnaire: Questionnaire | null;
	questionnaireStatus: QuestionnaireStatusEnum;
	rejectionReasons: [
		{
			key: string;
			title: string;
		}
	];
	questionnaireFailures: string[];
	language: string;
}

interface Verification extends BaseVerification, DocumentInformation {
	dateOfBirth: Date | null;
	issueDate: Date | null;
	expirationDate: Date | null;
	citizenship: string | null;
	isCompanyRepresentative: boolean | null;
	isLegalEntity: boolean | null;
	document: { comment: string; documentTypes: RequiredFiles[] };
	supportingDocuments: RequiredFiles[];
	stages: Stage[];
	publicNotes: string | null;
}

interface FetchedVerification extends BaseVerification, FetchedDocumentInformation {
	dateOfBirth: string | null;
	issueDate: string | null;
	expirationDate: string | null;
	citizenship: string | null;
	isNameProvidedByClient: boolean;
	isDocumentEditable: boolean;
	isEmailProvidedByClient: boolean;
	isCompanyRepresentative: boolean;
	isLegalEntity: boolean;
	document: { comment: string; documentTypes: RequiredFiles[] };
	stages: { stage: Stage; position: number }[];
	publicNotes: string | null;
}

export interface RequiredFiles {
	title: string;
	type: string;
	supportingDocuments: SupportingDocuments[];
	userComment: string;
}

export interface SupportingDocuments {
	createdAt: string;
	fileName: string;
	id: string;
	status: SupportingDocumentStatusEnum;
	title: string;
	type: string;
	isSuspicious: boolean;
}

export interface FetchedStage {
	stage: Stage;
	position: number;
}

export enum Stage {
	QUESTIONNAIRE = 'QUESTIONNAIRE',
	MEDIA = 'MEDIA',
	ADDITIONAL_QUESTIONS = 'ADDITIONAL_QUESTIONS',
	DOCUMENT_UPLOAD = 'DOCUMENT_UPLOAD',

	TIER_1 = 'TIER_1', // Bankera IB tiers
	TIER_2 = 'TIER_2',
	TIER_3 = 'TIER_3',
	TIER_BUSINESS = 'TIER_BUSINESS'
}

export interface Client {
	accentColor: string;
	disabledCountries: string[];
	title: string;
	displayTitle: string;
	successUrl: string;
	failureUrl: string;
	handoverUrl: string | null;
	minVerifiedAge: number | null;
	maxVerifiedAge: number | null;
	key: QuestionnaireKey;
	tooBrightThreshold: number | null;
	tooDarkThreshold: number | null;
	blurLevel: number | null;
	allowedDocuments: DocumentTypeAllowedCountries[];
	allowedLanguages: string[];
	isQuestionnairePrefillEnabled: boolean;
}

interface AppState {
	activePage: Page;
	activeCaptureObject: CaptureObject | null;
	isWaitingForVideo: boolean;
	waitingCaptureObject: CaptureObject | null;
	requiredCaptureObjects: CaptureObject[] | null;
	isSnapshotRequested: boolean;
	uploadFailureCount: number;
	isRequestingCameraPermission: boolean;
	hasCamera: boolean;
	hasCameraPermission: boolean;
	hasMultipleCameras: boolean;
	hasUserAgreement: boolean;
	verification: Verification | null;
	client: Client | null;
	languageList: string[];
	isQuestionnaireComplete: boolean;
	questionnaireLength: number;
	failureReason: string | null;
	isNameEditable: boolean;
	isDocumentEditable: boolean;
	isEmailEditable: boolean;
	cachedDateOfBirth: string | null;
	isPhoneNumberEditable: boolean;
	isAgreementEnabled: boolean;
	isSecondAgreementEnabled: boolean;
	isCameraResolutionTooLow: boolean;
	isMediaBeforeQuestionnaire: boolean;
	isRequiredCaptureObjectListCreated: boolean;
	isProgressBarEnabled: boolean;
	wasMediaUploaded: boolean;
	progressBarPosition: number;
	failedBlockValues?: { [key: string]: any } | null;
	isQuestionnaireUpdating: boolean;
	isSuccessTextShort: boolean;
	privacyPolicyLink: string;
	termsAndConditionsLink: string;
	isResidencePermitAvailable: boolean;
	language: string;
	languageData: Record<string, string>;
	wasDocumentUploadAdded: boolean;
	wasQuestionnaireStagesAdded: boolean;
	isMediaComplete: boolean;
	completedQuestionnaireStages: Stage[];
	areAdditionalQuestionsAdded: boolean;
	shouldAQsBeRecheckedForPrefillClient: boolean;
	isNewAdditionalQuestionsPresent: boolean;
	shouldShowHandoverUrl: boolean;
	isBNKOrPVKCorporate: boolean;
	isVerificationComplete: boolean;
	corporateVerifications: CorporateVerification[];
	viewState: ViewState | null;
	paymentInfo: PaymentInfo | null;
	gmapsLoaded: boolean;
}

interface AppProps {
	axios: AxiosInstance;
}

interface UploadObject {
	fileType: FileType;
	file: Blob;
	captureObject: CaptureObject;
	fileName?: string;
	deviceName: string;
}

export interface Progress {
	totalSteps: number;
	completedSteps: number;
	value: number;
}

export interface TINModel {
	id: string;
	countryCode: string;
	description: string;
	isTINOnPassport: boolean;
	isTINOnIDCard: boolean;
}

export type ThemeImages = {
	[key in KnownThemeImages]: React.FunctionComponent<SVGProps<SVGSVGElement>> | undefined;
};

export type CompleteThemeDefinition = {
	styles: {
		readonly [key: string]: string;
	};
	selectInput: {
		styles: Styles;
		theme: Partial<Theme>;
	};
	images: ThemeImages;
};

export type ThemeDefinition = {
	styles: {
		readonly [key: string]: string;
	};
	selectInput?: {
		styles?: Styles;
		theme?: Partial<Theme>;
	};
	images?: Partial<ThemeImages>;
};

export interface BrightnessThreshold {
	min: number;
	max: number;
}

export interface DocumentTypeAllowedCountries {
	documentType: DocumentType;
	allowedCountries: string[];
}

export interface ViewState {
	viewType: ViewTypeEnum;
	questionnaireKey: QuestionnaireKey;
	verificationStatus: VerificationStatusEnum;
}

function isVideoSupported(): boolean {
	return typeof MediaRecorder !== 'undefined';
}

function getThemeWithDefault(theme: ThemeDefinition): CompleteThemeDefinition {
	const mergedTheme = deepmerge(defaultTheme, theme) as CompleteThemeDefinition;
	// Images need to be merged shallow, so that svg elements are not deep merged
	mergedTheme.images = {
		...defaultTheme.images,
		...theme.images
	};

	if (theme.selectInput?.styles) {
		const themeSelectInputStyles = theme.selectInput.styles;
		mergedTheme.selectInput.styles = mergeSelectStyles(
			defaultTheme.selectInput.styles,
			themeSelectInputStyles
		);
	}
	return mergedTheme;
}

export const CompanyTitleContext = createContext('');

export const ThemeContext = createContext<CompleteThemeDefinition>(defaultTheme);

export const ProgressBarContext = createContext(true);

export const QuestionnaireUpdateContext = createContext(false);

export const DocumentIssuingCountryContext = createContext<string | null>(null);

export const Component = Sentry.withProfiler(
	withAxios(
		class App extends PureComponent<AppProps, AppState> {
			private readonly videoRef: React.RefObject<HTMLVideoElement>;

			private readonly openedPage: { current: null | Page };

			private readonly mediaMode: FileType = isVideoSupported()
				? FileType.VIDEO
				: FileType.PHOTO;

			private readonly uploadQueue: UploadObject[] = [];

			private mediaFilesList: UploadObject[] = [];

			private sessionKey = window.location.pathname.replace('/', '');

			private mediaStream: MediaStream | null = null;

			private mediaRecorder: MediaRecorder | null = null;

			private readonly intervalRecorder: number | null = null;

			private pendingVideo: {
				video: Blob;
				captureObject: CaptureObject | null;
			} | null = null;

			private cameraName = '';

			private isNewMediaSet = true;

			private videoCaptureObjects: CaptureObject[] | null = null;

			private isUploading = false;

			private introComponent: any = null;

			private questionnaireComponent: any = null;

			private agreementComponent: any = null;

			private secondAgreementComponent: any = null;

			private mediaComponent: any = null;

			private permissionComponent: any = null;

			private verificationHandoverComponent: any = null;

			private themeFile: CompleteThemeDefinition = defaultTheme;

			private readonly uploadedFiles: { image: string[]; video: string[] } = {
				image: [],
				video: []
			};

			private isComingFromApp = false;

			private appTotalSteps = 0;

			private isVerificationWithCountry = true;

			private isVerificationWithDocumentType = true;

			private isIntroPageEnabled = true;

			private isPermissionsPageEnabled = true;

			private isKYCApp = false;

			private locale: string | null = null;

			private isVerificationComplete = false;

			private videoDevices: MediaDeviceInfo[] = [];

			private readonly saveProgressEvent = new Event('saveProgress');

			private startFromApp: boolean | null = null;

			private continueFromApp: boolean | null = null;

			private boldDeskScript: HTMLScriptElement | null = null;

			public state = {
				activePage: Page.BLANK,
				activeCaptureObject: null as CaptureObject | null,
				isWaitingForVideo: false,
				waitingCaptureObject: null as CaptureObject | null,
				requiredCaptureObjects: null as CaptureObject[] | null,
				isSnapshotRequested: false,
				uploadFailureCount: 0,
				isRequestingCameraPermission: false,
				hasCamera: false,
				hasCameraPermission: false,
				cachedDateOfBirth: null,
				hasMultipleCameras: false,
				hasUserAgreement: false,
				verification: null as Verification | null,
				client: null as Client | null,
				languageList: [],
				isQuestionnaireComplete: false,
				questionnaireLength: 0,
				failureReason: null as string | null,
				isNameEditable: true,
				isDocumentEditable: true,
				isEmailEditable: true,
				isPhoneNumberEditable: true,
				isAgreementEnabled: true,
				isSecondAgreementEnabled: false,
				isCameraResolutionTooLow: false,
				isMediaBeforeQuestionnaire: false,
				isRequiredCaptureObjectListCreated: false,
				isProgressBarEnabled: true,
				wasMediaUploaded: false,
				progressBarPosition: 0,
				failedBlockValues: {},
				isQuestionnaireUpdating: false,
				isSuccessTextShort: false,
				privacyPolicyLink: '',
				termsAndConditionsLink: '',
				isResidencePermitAvailable: true,
				language: '',
				languageData: {},
				wasDocumentUploadAdded: false,
				wasQuestionnaireStagesAdded: false,
				isMediaComplete: false,
				completedQuestionnaireStages: [],
				areAdditionalQuestionsAdded: false,
				shouldAQsBeRecheckedForPrefillClient: true,
				isNewAdditionalQuestionsPresent: false,
				shouldShowHandoverUrl: false,
				isBNKOrPVKCorporate: false,
				isVerificationComplete: false,
				corporateVerifications: [],
				viewState: null,
				paymentInfo: null as PaymentInfo | null,
				gmapsLoaded: false
			};

			constructor(props: AppProps) {
				super(props);
				this.videoRef = React.createRef();
				this.openedPage = React.createRef();
			}

			UNSAFE_componentWillMount(): void {
				this.patchRootStyling();
				this.registerAppInterface();
			}

			componentDidMount(): void {
				Sentry.getCurrentScope().setTransactionName('VerificationView');
				Sentry.setTag('session_key', this.sessionKey);
				this.getQueryParams();
				this.authenticate()
					.then(() => this.getVerificationView())
					.then(() => this.logUserEvent(UserEvent.SESSION_STARTED))
					.then(this.checkIfUserHasCamera)
					.then(() => {
						if (this.sessionKey) {
							return this.fetchVerification().then(() => {
								this.getCorporateVerifications();
								// Temporary check if client is corporate. Required for promotional app banners
								// Should be removed when BANKERA_WALLET will have separate keys for private and corporate clients
								let hasFullLegalNameBlock = false;
								if (this.state.verification?.questionnaire?.[1]?.blocks) {
									const fullLegalNameBlock = this.state.verification
										?.questionnaire[1]?.blocks[0];
									hasFullLegalNameBlock = !!(
										fullLegalNameBlock?.key === 'FULL_LEGAL_NAME_BLOCK' &&
										fullLegalNameBlock?.fields[0]?.value
									);
								}

								if (
									this.state.client?.key === QuestionnaireKey.PERVESK_CORPORATE ||
									this.state.client?.key === QuestionnaireKey.BANKERA_KYB_2023 ||
									(this.state.client?.key === QuestionnaireKey.BANKERA_WALLET &&
										(this.state.verification?.stages.includes(
											Stage.TIER_BUSINESS
										) ||
											hasFullLegalNameBlock))
								) {
									this.setState({
										isBNKOrPVKCorporate: true
									});
								}

								window.addEventListener(
									'submitQuestionnaire',
									this.handleQuestionnaireSubmitOnConfirmation
								);

								if (
									window.location.href.includes('kycapp=true') &&
									this.state.client?.isQuestionnairePrefillEnabled
								) {
									return this.openPage(Page.LOADING);
								}
								return this.checkForAdditionalQuestions().then(() => {
									// Submit verification if nothing else is required from user

									if (this.continueFromApp) {
										return this.continueFromAppFlow();
									}

									if (
										this.state.verification?.stages.length === 0 &&
										!this.state.areAdditionalQuestionsAdded
									) {
										return this.openPage(Page.CONFIRMATION);
									}

									if (
										this.state.client?.key.includes('SPECTROCOIN') &&
										!this.state.client?.key.includes('LOANS')
									) {
										this.isIntroPageEnabled = false;
										return this.handleIntroNextClick();
									}

									return this.openPage(Page.INTRO);
								});
							});
						}

						const urlLanguage = queryString.parse(window.location.search).lang;
						if (urlLanguage && typeof urlLanguage === 'string') {
							this.handleLanguageChange(urlLanguage.substr(0, 2).toLowerCase());
						} else {
							this.handleLanguageChange(Language.EN);
						}

						return this.openPage(Page.ENTRY);
					})
					.catch((error: Error) => {
						if (
							error?.message === 'VERIFICATION_UNDER_REVIEW' ||
							error?.message === 'VERIFICATION_ONBOARDED'
						) {
							// do nothing
							// If window is redirecting, it shouldn't update the app anymore
						} else if (!window.isRedirecting && error) {
							this.fail(error.message, VerificationFailureReason.AUTHENTICATION);
						}
					});
				this.registerResizeCallback();
			}

			componentDidUpdate(_: AppProps, prevState: AppState): void {
				// Compare state variables and update Sentry tags only if they change
				if (
					prevState.activePage !== this.state.activePage ||
					prevState.isVerificationComplete !== this.state.isVerificationComplete ||
					prevState.language !== this.state.language
				) {
					Sentry.setTags({
						active_page: this.state.activePage,
						is_verification_complete: this.state.isVerificationComplete,
						language: this.state.language || 'unknown'
					});
				}

				if (prevState.client?.key !== this.state.client?.key && this.state.client) {
					Sentry.setTag('questionnaire_key', this.state.client.key);
				}

				if (
					prevState.isRequestingCameraPermission !==
						this.state.isRequestingCameraPermission ||
					prevState.hasCamera !== this.state.hasCamera ||
					prevState.hasCameraPermission !== this.state.hasCameraPermission ||
					prevState.hasMultipleCameras !== this.state.hasMultipleCameras ||
					prevState.isCameraResolutionTooLow !== this.state.isCameraResolutionTooLow
				) {
					Sentry.setContext('Camera Info', {
						isRequestingCameraPermission: this.state.isRequestingCameraPermission,
						hasCamera: this.state.hasCamera,
						hasCameraPermission: this.state.hasCameraPermission,
						hasMultipleCameras: this.state.hasMultipleCameras,
						isCameraResolutionTooLow: this.state.isCameraResolutionTooLow
					});
				}
			}

			componentWillUnmount(): void {
				this.unregisterResizeCallback();
				window.removeEventListener(
					'submitQuestionnaire',
					this.handleQuestionnaireSubmitOnConfirmation
				);

				if (this.boldDeskScript) {
					document.head.removeChild(this.boldDeskScript);
					this.boldDeskScript = null;
				}
			}

			getQueryParams = () => {
				const params = new URLSearchParams(window.location.search);
				this.startFromApp = params.get('start-from-app') === 'true';
				this.continueFromApp = params.get('continue-from-app') === 'true';
				if (this.startFromApp || this.continueFromApp) {
					this.isKYCApp = params.get('is-kyc-app') === 'true';
				}
			};

			continueFromAppFlow = () => {
				const params = new URLSearchParams(window.location.search);
				const totalSteps = params.get('total-steps');
				const currentProgress = params.get('current-progress');

				this.isComingFromApp = true;
				this.appTotalSteps = Number(totalSteps ?? 0);
				this.increaseProgress(Number(currentProgress ?? 0) + 1);

				this.setState({ hasCameraPermission: true }, () => {
					if (this.state.client?.isQuestionnairePrefillEnabled) {
						return this.setState({ isVerificationComplete: true }, () =>
							this.completeVerification()
						);
					}

					if (this.isQuestionnaire()) {
						return this.openPage(Page.QUESTIONNAIRE);
					}
					if (this.state.isSecondAgreementEnabled) {
						this.agreementComponent = this.secondAgreementComponent;
						return this.openPage(Page.AGREEMENT);
					}
					this.openPage(Page.LOADING);
					return this.completeVerification();
				});
			};

			getPaymentInfo = async () => {
				try {
					const { data: paymentInfo } = await this.props.axios.get<PaymentInfo>(
						'/payment-info',
						{
							params: {
								sessionToken: this.sessionKey
							}
						}
					);

					this.setPaymentInfo(paymentInfo);

					if (
						paymentInfo &&
						Object.keys(paymentInfo).length > 0 &&
						(((paymentInfo.totalApplicationFeeStatus ?? '') ===
							FeeStatusEnum.NOT_PAID &&
							(paymentInfo.totalApplicationFee ?? 0) > 0 &&
							(paymentInfo.totalApplicationFeeWithDiscount ?? 0) > 0) ||
							paymentInfo.totalApplicationFeeStatus ===
								FeeStatusEnum.PENDING_EXTERNAL_PAYMENT)
					) {
						return true;
					}

					return false;
				} catch (err) {
					this.fail(
						getAxiosErrorDefaultMessage(err),
						VerificationFailureReason.FAILED_UPDATE
					);
					return false;
				}
			};

			getVerificationView = async () => {
				if (!this.sessionKey) {
					return;
				}

				const { data } = await this.props.axios.get<ViewState>(
					`/verifications/${this.sessionKey}/view`
				);
				const { verificationStatus, questionnaireKey } = data;
				Sentry.setTag('questionnaire_key', questionnaireKey);
				const underReviewtypes = [
					VerificationStatusEnum.SUBMITTED,
					VerificationStatusEnum.NEED_ATTENTION,
					VerificationStatusEnum.AUTO_COMPLETED
				];

				if (underReviewtypes.includes(verificationStatus)) {
					const theme = await this.getClientTheme(questionnaireKey);
					this.themeFile = getThemeWithDefault(theme.default);
					this.setMetaTitle(questionnaireKey);
					this.setFavicon(questionnaireKey);

					const shouldApplicationPaymentBeVisible = await this.getPaymentInfo();

					if (shouldApplicationPaymentBeVisible) {
						this.openPage(Page.APPLICATION_PAYMENT);
					} else {
						this.setState({
							viewState: data,
							activePage: Page.UNDER_REVIEW
						});
					}

					throw new Error('VERIFICATION_UNDER_REVIEW');
				} else if (verificationStatus === VerificationStatusEnum.ONBOARDED) {
					const theme = await this.getClientTheme(questionnaireKey);
					this.themeFile = getThemeWithDefault(theme.default);
					this.setMetaTitle(questionnaireKey);
					this.setFavicon(questionnaireKey);

					const shouldApplicationPaymentBeVisible = await this.getPaymentInfo();
					if (shouldApplicationPaymentBeVisible) {
						this.openPage(Page.APPLICATION_PAYMENT);
						throw new Error('VERIFICATION_ONBOARDED');
					}
				}
			};

			setPaymentInfo = (paymentInfo: PaymentInfo) => {
				this.setState({
					paymentInfo
				});
			};

			getCorporateVerifications = async () => {
				try {
					const { data } = await this.props.axios.get(
						`/corporate-verifications/${this.sessionKey}/related-verifications`
					);
					this.setState({
						corporateVerifications: data
					});
				} catch {
					// do nothing
				}
			};

			handleLanguageChange = (lang: string) => {
				if (isRunningInIFrame()) {
					window.parent.postMessage({ language: this.locale }, '*');
				}
				this.setLocale(lang);
			};

			initGoogleMaps = () => {
				this.setState({
					gmapsLoaded: true
				});
			};

			setLocale = (language: string) => {
				// set maps api with locale if it was not set before. Changing script params without refresh causes crashes so only set once
				const oldTag = document.querySelector(
					`script[src*="${config.GOOGLE_MAPS_API_KEY}"]`
				);
				if (!oldTag) {
					window.initGoogleMaps = this.initGoogleMaps;
					const scriptTag = document.createElement('script');
					scriptTag.src = `https://maps.googleapis.com/maps/api/js?key=${config.GOOGLE_MAPS_API_KEY}&libraries=places,geocoding&language=${language}&callback=initGoogleMaps&loading=async`;
					scriptTag.async = true;
					document.body.appendChild(scriptTag);
				}

				if (!isRunningInIFrame()) {
					localStorage.setItem('lang', language);
				}
				window.dispatchEvent(this.saveProgressEvent);
				this.setState({
					language
				});
				this.changeTranslations(language);
				document.body.dir = language === 'ar' ? 'rtl' : 'ltr';
				this.locale = language;
			};

			callNativeMobileCamera = async () => {
				const { client, verification } = this.state;

				if (client?.key && verification?.documentType) {
					const { key } = client;
					const { documentType } = verification;

					if (this.startFromApp && window.ReactNativeWebView) {
						await waitForInflightMutations();
						return window.ReactNativeWebView.postMessage(
							JSON.stringify({
								eventId: WebViewEventIdEnum.REQUEST_NATIVE_CAMERA,
								data: {
									clientKey: key,
									documentType: documentType || DocumentType.PASSPORT,
									currentProgress: this.state.progressBarPosition,
									totalSteps: this.getTotalSteps(),
									isProgressBarEnabled: this.state.isProgressBarEnabled
								}
							})
						);
					}

					if (window?.handler?.onNativeCameraRequested) {
						return window?.handler?.onNativeCameraRequested(
							key,
							documentType || DocumentType.PASSPORT,
							this.state.progressBarPosition,
							this.getTotalSteps()
						);
					}

					return null;
				}

				return null;
			};

			getCameraConstraints = (facingMode = 'environment', deviceId = '') => {
				if (!isMobile()) {
					return {
						audio: true,
						video: {
							facingMode
						}
					};
				}
				if (isDeviceIOS()) {
					return {
						audio: true,
						video: {
							// iOS needs exact to switch camera
							facingMode: facingMode === 'user' ? { exact: facingMode } : facingMode
						}
					};
				}

				if (!isFirefoxOnAndroid() && deviceId === '' && facingMode === 'environment') {
					// Select last camera from back cameras as it likely won't be wide lens
					// or some other custom camera.
					const backCameras = this.videoDevices.filter(vd => vd.label.includes('back'));
					if (backCameras.length > 0) {
						return {
							audio: true,
							video: {
								deviceId: {
									exact: backCameras?.at(-1)?.deviceId
								}
							}
						};
					}
				}
				return {
					audio: true,
					video: deviceId ? { deviceId } : { facingMode }
				};
			};

			restartCamera = (facingMode = 'environment') => {
				return new Promise<void>((resolve, reject) => {
					this.requestCameraPermission(facingMode)
						.then(() => resolve())
						.catch(() => reject());
				});
			};

			tryDifferentCamera = (
				facingMode: string,
				resolve: () => void,
				reject: (value?: string) => void
			) => {
				if (facingMode === 'environment') {
					this.videoDevices = this.videoDevices.filter(device =>
						device.label.includes('back')
					);
					this.requestCameraPermission(facingMode, this.videoDevices[0].deviceId)
						.then(() => {
							this.cameraName = this.videoDevices[0].label;
							return resolve();
						})
						.catch(() => {
							this.videoDevices.shift();
							if (this.videoDevices.length > 0) {
								this.tryDifferentCamera(facingMode, resolve, reject);
							} else {
								reject();
							}
						});
				} else if (facingMode === 'user') {
					this.videoDevices = this.videoDevices.filter(device =>
						device.label.includes('front')
					);
					this.requestCameraPermission(facingMode, this.videoDevices[0].deviceId)
						.then(() => {
							this.cameraName = this.videoDevices[0].label;
							return resolve();
						})
						.catch(() => {
							this.videoDevices.shift();
							if (this.videoDevices.length > 0) {
								this.tryDifferentCamera(facingMode, resolve, reject);
							} else {
								reject();
							}
						});
				}
			};

			requestCameraPermission = (facingMode = 'environment', deviceId = '') => {
				if (this.mediaStream) {
					// stop previous video track and media stream to switch camera
					this.mediaStream.getVideoTracks()[0].stop();
					this.interruptRecording(false);
				}
				if (window.handler?.onNativeCameraRequested || this.startFromApp) {
					return new Promise<void>(resolve => {
						this.setState(
							{
								hasCameraPermission: true,
								hasMultipleCameras: false
							},
							resolve
						);
					});
				}
				return new Promise<void>((resolve, reject) => {
					if (!this.state.isRequestingCameraPermission) {
						this.logUserEvent(UserEvent.CAMERA_ACCESS_PROMPTED);
						this.setState(
							{
								isRequestingCameraPermission: true
							},
							() => {
								navigator.mediaDevices
									?.getUserMedia(this.getCameraConstraints(facingMode, deviceId))
									.then(stream => {
										if (this.mediaStream !== stream) {
											this.mediaStream = stream;

											if (this.mediaMode === FileType.VIDEO) {
												this.mediaRecorder = new MediaRecorder(stream, {
													mimeType: getSupportedMimeType(true),
													videoBitsPerSecond: 250000
												});
											}
											const filteredVideoDevices: MediaDeviceInfo[] = [];
											let videoDevices: MediaDeviceInfo[] = [];

											navigator.mediaDevices
												.enumerateDevices()
												.then(devices => {
													videoDevices = [...devices].filter(
														device => device.kind === 'videoinput'
													);

													videoDevices.forEach(device => {
														let isEntryDuplicate = false;
														filteredVideoDevices.forEach(
															filteredVideoDevice => {
																if (
																	filteredVideoDevice.deviceId ===
																	device.deviceId
																) {
																	isEntryDuplicate = true;
																}
															}
														);
														if (!isEntryDuplicate) {
															filteredVideoDevices.push(device);
														}
													});

													if (this.videoDevices.length === 0) {
														this.videoDevices = filteredVideoDevices;
													}

													const videoInputCount =
														filteredVideoDevices.length;

													// feature is temporary switched off
													this.startRecording();
													this.logUserEvent(
														UserEvent.CAMERA_ACCESS_GRANTED
													);
													this.setState(
														{
															isRequestingCameraPermission: false,
															hasCameraPermission: true,
															hasMultipleCameras: videoInputCount > 1
														},
														resolve
													);
												});
											this.cameraName = this.mediaStream.getVideoTracks()[0].label;
											this.mediaStream.getVideoTracks()[0].onended = () => {
												this.logUserEvent(
													UserEvent.CAMERA_DISCONNECTED
												).then(() =>
													this.requestCameraPermission(facingMode)
												);
											};
										} else {
											this.setState(
												{
													isRequestingCameraPermission: false
												},
												resolve
											);
										}
									})
									.catch(error => {
										Sentry.captureException(error);
										this.logUserEvent(UserEvent.WEBCAM_ACCESS_REJECTED);
										if (error.name === 'OverconstrainedError') {
											this.setState(
												{
													isRequestingCameraPermission: false,
													hasCameraPermission: false,
													isCameraResolutionTooLow: true
												},
												reject
											);
										} else if (error.name === 'NotReadableError') {
											if (!deviceId) {
												this.setState({
													isRequestingCameraPermission: false
												});
												navigator.mediaDevices
													?.enumerateDevices()
													.then(devices => {
														this.videoDevices = [...devices].filter(
															device => device.kind === 'videoinput'
														);
														if (this.videoDevices.length > 1) {
															return this.tryDifferentCamera(
																facingMode,
																resolve,
																reject
															);
														}
														return this.fail(
															'Please check if your camera is being used by another app and try again',
															VerificationFailureReason.CAMERA_BEING_USED
														);
													});
											} else {
												this.setState(
													{
														isRequestingCameraPermission: false,
														hasCameraPermission: false
													},
													reject
												);
											}
										} else {
											this.setState(
												{
													isRequestingCameraPermission: false,
													hasCameraPermission: false
												},
												reject
											);
										}
									});
							}
						);
					}
				});
			};

			interruptRecording = (shouldContinue = true) => {
				if (this.mediaRecorder) {
					if (this.state.isSnapshotRequested) {
						const captureObjectOnSnapshot = this.state.activeCaptureObject;
						this.mediaRecorder.ondataavailable = event => {
							const captureObject =
								captureObjectOnSnapshot || this.state.waitingCaptureObject;
							if (event?.data) {
								this.pendingVideo = { video: event.data, captureObject };
							}
							if (this.state.isWaitingForVideo && captureObject) {
								this.setState(
									prevState => ({
										isWaitingForVideo: !event.data.size,
										waitingCaptureObject: event.data.size
											? null
											: prevState.waitingCaptureObject
									}),
									() => {
										if (event?.data) {
											this.saveVideo(
												captureObject,
												event.data,
												`webVideo.${
													MediaRecorder?.isTypeSupported('video/webm')
														? 'webm'
														: 'mp4'
												}`
											);
										}
										if (!this.state.activeCaptureObject) {
											this.stopRecording();
										}
									}
								);
							}
						};
						this.setState(
							{
								isSnapshotRequested: false
							},
							() => {
								if (this.mediaRecorder) {
									if (shouldContinue) {
										this.restartRecording();
									} else {
										this.stopRecording();
									}
								}
							}
						);
					} else if (shouldContinue) {
						this.restartRecording();
					} else {
						this.stopRecording();
					}
				}
			};

			startRecording = () => {
				this.setState(
					{
						isSnapshotRequested: false
					},
					() => {
						if (this.mediaRecorder && this.mediaRecorder.state !== 'recording') {
							this.mediaRecorder.start();
						}
					}
				);
			};

			restartRecording = () => {
				if (this.mediaRecorder && this.mediaRecorder.state !== 'recording') {
					this.mediaRecorder.stop();
					this.mediaRecorder.start();
				}
			};

			stopRecording = () => {
				if (this.intervalRecorder) {
					clearInterval(this.intervalRecorder);
				}
				if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
					this.mediaRecorder.stop();
				}
				if (this.mediaStream) {
					this.mediaStream.getVideoTracks()[0].stop();
				}
			};

			stopMedia = () => {
				if (this.mediaStream) {
					this.mediaStream.getVideoTracks()[0].stop();
				}
				if (this.mediaRecorder) {
					this.mediaRecorder.stop();
				}
				this.mediaStream = null;
				this.mediaRecorder = null;
			};

			startMedia = (facingMode = 'environment', deviceId = '') => {
				navigator.mediaDevices
					?.getUserMedia(this.getCameraConstraints(facingMode, deviceId))
					.then(stream => {
						if (this.mediaStream !== stream) {
							this.mediaStream = stream;

							if (this.mediaMode === FileType.VIDEO) {
								this.mediaRecorder = new MediaRecorder(stream, {
									mimeType: getSupportedMimeType(true),
									videoBitsPerSecond: 250000
								});
								const captureObjectOnSnapshot = this.state.activeCaptureObject;
								this.mediaRecorder.ondataavailable = event => {
									const captureObject =
										captureObjectOnSnapshot || this.state.waitingCaptureObject;
									if (event?.data) {
										this.pendingVideo = { video: event.data, captureObject };
									}
									if (this.state.isWaitingForVideo && captureObject) {
										this.setState(
											prevState => ({
												isWaitingForVideo: !event.data.size,
												waitingCaptureObject: event.data.size
													? null
													: prevState.waitingCaptureObject
											}),
											() => {
												if (event?.data) {
													this.saveVideo(
														captureObject,
														event.data,
														`webVideo.${
															MediaRecorder?.isTypeSupported(
																'video/webm'
															)
																? 'webm'
																: 'mp4'
														}`
													);
												}
												if (!this.state.activeCaptureObject) {
													this.stopMedia();
												}
											}
										);
									}
								};
							}
						}
					});
			};

			takeSnapshot = () => {
				this.increaseProgress();
				if (this.mediaMode === FileType.VIDEO && this.mediaRecorder) {
					this.setState({
						isSnapshotRequested: true
					});
				}
			};

			handleConfirm = () => {
				this.increaseProgress();
				const captureObject =
					this.pendingVideo?.captureObject || this.state.activeCaptureObject;
				if (this.pendingVideo?.video && captureObject) {
					this.saveVideo(
						captureObject,
						this.pendingVideo.video,
						`webVideo.${MediaRecorder?.isTypeSupported('video/webm') ? 'webm' : 'mp4'}`
					);
					this.pendingVideo = null;
					this.promptNextDocument();
				} else {
					if (
						this.mediaStream &&
						this.state.activeCaptureObject === CaptureObject.SELFIE
					) {
						// stop previous video track and media stream to switch camera
						this.mediaStream.getVideoTracks()[0].stop();
						this.interruptRecording(false);
					}
					this.setState(
						prevState => ({
							isWaitingForVideo: this.mediaMode === FileType.VIDEO,
							waitingCaptureObject: prevState.activeCaptureObject
						}),
						this.promptNextDocument
					);
				}
			};

			saveVideo = (captureObject: CaptureObject, file: Blob, fileName: string) => {
				if (file.size >= 1000) {
					const videoCaptureObject = this.videoCaptureObjects?.shift();
					this.uploadQueue.push({
						file,
						captureObject: videoCaptureObject || captureObject,
						fileType: FileType.VIDEO,
						fileName,
						deviceName: this.cameraName
					});
					this.mediaFilesList.push({
						file,
						captureObject: videoCaptureObject || captureObject,
						fileType: FileType.VIDEO,
						fileName,
						deviceName: this.cameraName
					});
				}
				this.triggerUpload();
			};

			saveImage = (captureObject: CaptureObject, file: Blob) => {
				this.uploadQueue.push({
					file,
					captureObject,
					fileType: FileType.PHOTO,
					deviceName: this.cameraName
				});
				this.mediaFilesList.push({
					file,
					captureObject,
					fileType: FileType.PHOTO,
					deviceName: this.cameraName
				});
				this.triggerUpload();
			};

			logUploadedMedia = (captureObject: CaptureObject): Promise<void> => {
				return new Promise<void>(resolve => {
					switch (captureObject) {
						case CaptureObject.SELFIE:
							this.logUserEvent(UserEvent.SELFIE_UPLOADED).then(() => {
								resolve();
							});
							break;
						case CaptureObject.ID_BACK:
						case CaptureObject.PASSPORT_COVER:
						case CaptureObject.RESIDENCE_PERMIT_BACK:
						case CaptureObject.DRIVERS_LICENCE_BACK:
							this.logUserEvent(UserEvent.DOCUMENT_BACK_SIDE_UPLOADED).then(() => {
								resolve();
							});
							break;
						case CaptureObject.ID_FRONT:
						case CaptureObject.PASSPORT_PAGE:
						case CaptureObject.RESIDENCE_PERMIT_FRONT:
						case CaptureObject.DRIVERS_LICENCE_FRONT:
							this.logUserEvent(UserEvent.DOCUMENT_FRONT_SIDE_UPLOADED).then(() => {
								resolve();
							});
							break;
						default:
							resolve();
					}
				});
			};

			isNewSet = (captureObject: CaptureObject, fileType: FileType) => {
				if (!window.handler?.onNativeCameraRequested && this.isNewMediaSet === false) {
					return 'false';
				}
				if (this.isNewMediaSet) {
					this.isNewMediaSet = false;
				}
				switch (captureObject) {
					case CaptureObject.ID_FRONT:
					case CaptureObject.PASSPORT_PAGE:
					case CaptureObject.RESIDENCE_PERMIT_FRONT:
					case CaptureObject.DRIVERS_LICENCE_FRONT:
						return `${captureObject === this.mediaFilesList[0]?.captureObject &&
							fileType === this.mediaFilesList[0]?.fileType}`;
					default:
						return 'false';
				}
			};

			completeUpload = (captureObject: CaptureObject) => {
				if (
					this.uploadedFiles.video.includes(captureObject) &&
					this.uploadedFiles.image.includes(captureObject) &&
					window?.handler?.onMediaUploadSuccess
				) {
					window.handler.onMediaUploadSuccess();
				}
				this.isUploading = false;
				if (this.uploadQueue.length) {
					return this.triggerUpload();
				}
				if (
					this.state.activePage === Page.LOADING &&
					(!this.state.requiredCaptureObjects ||
						!this.state.requiredCaptureObjects.length)
				) {
					if (this.state.isSecondAgreementEnabled) {
						this.agreementComponent = this.secondAgreementComponent;
						return this.openPage(Page.AGREEMENT);
					}
					if (this.state.client?.isQuestionnairePrefillEnabled) {
						this.props.axios
							.get(`/verifications/${this.sessionKey}`)
							.then(({ data: response }: AxiosResponse<FetchedVerification>) => {
								this.updateStages(response)
									.then(() => {
										return this.setState(
											{
												isVerificationComplete: true,
												isMediaComplete: true
											},
											() => this.completeVerification()
										);
									})
									.catch(() => null);
							})
							.catch(error => {
								console.error(error);
							});
					}
					return this.setState(
						{
							isMediaComplete: true
						},
						() => this.completeVerification()
					);
				}
				return null;
			};

			triggerUpload = () => {
				if (!this.isUploading && this.uploadQueue.length) {
					this.isUploading = true;
					// Upload
					const mediaTuple = this.uploadQueue.shift();
					if (mediaTuple) {
						this.uploadFile(mediaTuple)
							.then(() => {
								this.setState({ wasMediaUploaded: true });
								if (mediaTuple.fileType === FileType.PHOTO) {
									this.uploadedFiles.image.push(mediaTuple.captureObject);
									this.logUploadedMedia(mediaTuple.captureObject).then(() => {
										this.completeUpload(mediaTuple.captureObject);
									});
								} else if (mediaTuple.fileType === FileType.VIDEO) {
									this.pendingVideo = null;
									this.uploadedFiles.video.push(mediaTuple.captureObject);
									this.completeUpload(mediaTuple.captureObject);
								}
							})
							.catch(error => {
								Sentry.captureException(error);

								if (
									mediaTuple.captureObject ===
										this.mediaFilesList[0]?.captureObject &&
									mediaTuple.fileType === this.mediaFilesList[0]?.fileType
								) {
									this.isNewMediaSet = true;
								}

								if (
									mediaTuple.fileType === FileType.VIDEO ||
									(mediaTuple.fileType === FileType.PHOTO &&
										this.state.uploadFailureCount >= 3)
								) {
									this.mediaFilesList = this.mediaFilesList.filter(
										singleFile =>
											singleFile.captureObject !== mediaTuple.captureObject ||
											singleFile.fileType !== mediaTuple.fileType
									);
								}
								if (
									mediaTuple.fileType === FileType.PHOTO &&
									window?.handler?.onImageUploadFailure
								) {
									window.handler.onImageUploadFailure(
										error.response?.data?.errorCode || '',
										error.response?.data?.defaultMessage ||
											error?.defaultMessage ||
											error?.message
									);
								} else if (
									mediaTuple.fileType === FileType.VIDEO &&
									// remove video failure since app crashes on this flow and there is noone to fix it
									window?.handler?.onVideoUploadFailure
								) {
									this.uploadedFiles.video.push(mediaTuple.captureObject);
									// this.completeUpload(mediaTuple.captureObject);
									window.handler.onVideoUploadFailure(
										error.response?.data?.errorCode || error?.errorCode || '',
										error.response?.data?.defaultMessage ||
											error?.message ||
											error?.defaultMessage ||
											''
									);
								}
								if (
									this.state.uploadFailureCount < 3 &&
									// removed from video upload failure flow
									mediaTuple.fileType !== FileType.VIDEO
								) {
									this.setState(prevState => ({
										uploadFailureCount: prevState.uploadFailureCount + 1
									}));
									this.uploadQueue.unshift(mediaTuple);
									this.videoCaptureObjects?.unshift(mediaTuple.captureObject);
									this.isUploading = false;
									this.triggerUpload();
									// removed from video upload failure flow
								} else if (mediaTuple.fileType !== FileType.VIDEO) {
									const errorDefaultMessage = error.response?.data?.defaultMessage
										? `. ${error.response?.data?.defaultMessage}`
										: '';
									this.fail(
										`Failed to upload media: ${mediaTuple.captureObject} ${mediaTuple.fileType}${errorDefaultMessage}`,
										VerificationFailureReason.FAILED_MEDIA_UPLOAD
									);
								}
							});
					}
				} else if (
					this.state.wasMediaUploaded &&
					this.state.isQuestionnaireComplete &&
					this.state.isMediaBeforeQuestionnaire &&
					(!this.state.requiredCaptureObjects ||
						!this.state.requiredCaptureObjects.length)
				) {
					if (this.state.isSecondAgreementEnabled) {
						this.agreementComponent = this.secondAgreementComponent;
						this.openPage(Page.AGREEMENT);
					} else {
						this.completeVerification();
					}
				}
			};

			uploadFile = (uploadObject: UploadObject) => {
				const fileBlob = new Blob([uploadObject.file], { type: getSupportedMimeType() });
				const formData = new FormData();

				formData.append('fileType', uploadObject.fileType);
				formData.append('mediaType', uploadObject.captureObject);
				formData.append('file', fileBlob, uploadObject?.fileName);
				formData.append('deviceName', uploadObject.deviceName);
				formData.append(
					'isNewSet',
					this.isNewSet(uploadObject.captureObject, uploadObject.fileType)
				);
				return this.props.axios.post(`/verifications/${this.sessionKey}/media`, formData, {
					headers: {
						'content-type': 'multipart/form-data'
					},
					responseType: 'text',
					transformResponse: [
						data => {
							try {
								return JSON.parse(data);
							} catch (err) {
								return undefined;
							}
						}
					] // parse to json only if data is present
				});
			};

			updateCompletedQuestionnaireStages = () => {
				if (this.state.verification?.stages) {
					return this.setState(prevState => ({
						completedQuestionnaireStages:
							prevState.verification?.stages.filter(
								stage => stage !== Stage.DOCUMENT_UPLOAD && stage !== Stage.MEDIA
							) || []
					}));
				}
				return undefined;
			};

			updateStages = (fetchedVerification: FetchedVerification) => {
				const stages = sortStages(fetchedVerification.stages);

				const wasDocUploadStageAdded =
					!this.state.verification?.stages.includes(Stage.DOCUMENT_UPLOAD) &&
					!!fetchedVerification.stages.find(
						stage => stage.stage === Stage.DOCUMENT_UPLOAD
					);
				const currentRequestedFilesCount =
					this.state.verification?.supportingDocuments?.length || 0;
				const newRequestedFilesCount =
					fetchedVerification.document?.documentTypes?.length || 0;

				const newQuestionnaireStages = stages.filter(
					newStage =>
						!this.state.verification?.stages.includes(newStage) &&
						newStage !== Stage.MEDIA &&
						newStage !== Stage.DOCUMENT_UPLOAD
				);

				const wasQuestionnaireStagesAdded =
					this.state.wasQuestionnaireStagesAdded || !!newQuestionnaireStages.length;

				const wasDocumentUploadAdded =
					this.state.wasDocumentUploadAdded ||
					wasDocUploadStageAdded ||
					currentRequestedFilesCount < newRequestedFilesCount;
				return new Promise<void>((resolve, reject) => {
					if (
						(wasDocumentUploadAdded || wasQuestionnaireStagesAdded) &&
						this.state.verification
					) {
						this.setState(
							prevState => ({
								verification: {
									...prevState.verification!,
									supportingDocuments:
										fetchedVerification.document?.documentTypes,
									stages
								},
								wasDocumentUploadAdded,
								wasQuestionnaireStagesAdded,
								isVerificationComplete: false
							}),
							() => resolve()
						);
					} else {
						reject();
					}
				});
			};

			submitVerification = () => {
				this.setState({
					isQuestionnaireUpdating: true
				});

				return this.props.axios
					.put(`/verifications/${this.sessionKey}`, this.getVerificationFromState())
					.then(({ data: response }: AxiosResponse<FetchedVerification>) => {
						this.updateStages(response).catch(() => null);
						if (!this.state.areAdditionalQuestionsAdded) {
							this.checkForAdditionalQuestions();
						}
						this.triggerUpload();
					})
					.catch(error => {
						const errorCode = error.response?.data?.errorCode || error?.errorCode || '';
						const errorMessage =
							error.response?.data?.defaultMessage || error?.message || '';

						if (
							(this.startFromApp || this.continueFromApp) &&
							window.ReactNativeWebView
						) {
							window.ReactNativeWebView.postMessage(
								JSON.stringify({
									eventId: WebViewEventIdEnum.VERIFICATION_FAILURE,
									data: {
										errorCode,
										errorMessage
									}
								})
							);
						} else if (window.handler?.onVerificationFailure) {
							window.handler.onVerificationFailure(errorCode, errorMessage);
						}
						if (error.response?.data?.key === 'questionnaire_is_in_incorrect_status') {
							return this.fail(
								'Your questionnaire is already submitted please contact support',
								VerificationFailureReason.FAILED_UPDATE
							);
						}
						return this.fail(
							'Failed to upload verification',
							VerificationFailureReason.FAILED_UPDATE
						);
					});
			};

			submitDocumentInformation = () =>
				this.props.axios
					.put(
						`/verifications/${this.sessionKey}/document-info`,
						this.getDocumentInformationFromState()
					)
					.then(({ data: response }: AxiosResponse<FetchedVerification>) => {
						this.updateStages(response).catch(() => null);
					})
					.catch(() => {
						return this.fail(
							'Failed to upload document information',
							VerificationFailureReason.FAILED_UPDATE
						);
					});

			openPage = (activePage: Page) => {
				if (isMobileApp() && activePage === Page.INTERIM_QR) {
					this.handleInterimQRNextClick();
				} else {
					this.setState({
						activePage,
						isQuestionnaireUpdating: false
					});
				}
			};

			openVerification = () => {
				window.history.pushState({}, '', this.sessionKey);
				this.openPage(Page.INTRO);
				this.increaseProgress();
			};

			fail = (
				failureReason: string | null = null,
				failureType: VerificationFailureReason
			) => {
				this.detachPageBlocker();
				if (isRunningInIFrame()) {
					// Post message to parent window, if running in an iframe
					window.parent.postMessage('failure', '*');
					window.parent.postMessage({ event: 'failure', reason: failureType }, '*');
				}
				this.setState({
					failureReason,
					activePage: Page.FAILURE
				});
			};

			authenticate = () => {
				return this.props.axios.post(`/proxy/client_login`).then(response => {
					const { data } = response;
					const { access_token: accessToken, exp } = data;
					const currentTimeMs = Date.now();

					if (accessToken && exp) {
						const expMs = exp * 1000;
						sessionStorage.setItem('ACCESS_TOKEN', accessToken);
						sessionStorage.setItem('EXPIRATION_TIME_MS', String(expMs));

						const timeUntilExpiration = expMs - currentTimeMs - 30000;
						setTimeout(this.authenticate, timeUntilExpiration);
					}
					window.isAuthenticated = true;
					return response;
				});
			};

			checkIfUserHasCamera = () =>
				new Promise<void>(resolve => {
					if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
						navigator.mediaDevices
							.enumerateDevices()
							.then(devices => {
								const videoDevices = [...devices].filter(
									device => device.kind === 'videoinput'
								);
								const videoInputCount = videoDevices.length;
								if (videoInputCount) {
									this.logUserEvent(UserEvent.USER_HAS_WEBCAM);
									this.setState(
										{
											hasCamera: true
										},
										resolve
									);
								} else {
									this.logUserEvent(UserEvent.NO_WEBCAM);
									resolve();
								}
							})
							.catch(() => {
								this.logUserEvent(UserEvent.NO_WEBCAM);
								resolve();
							});
					} else {
						this.logUserEvent(UserEvent.NO_WEBCAM);
						resolve();
					}
				});

			setMetaTitle = (key: QuestionnaireKey) => {
				if (
					[
						QuestionnaireKey.BANKERA_WALLET,
						QuestionnaireKey.BANKERA_KYB_2023,
						QuestionnaireKey.BANKERA_PRIVATE_2023
					].includes(key)
				) {
					document.title = 'Bankera Questionnaire';
				}
			};

			setFavicon = (key: QuestionnaireKey) => {
				let favicon = document.querySelector<HTMLLinkElement>("link[rel='icon']");
				if (!favicon) {
					favicon = document.createElement('link');
					favicon.rel = 'icon';
					document.getElementsByTagName('head')[0].appendChild(favicon);
				}
				switch (key) {
					case QuestionnaireKey.BANKERA_LOANS:
					case QuestionnaireKey.BANKERA_MEDIA:
					case QuestionnaireKey.BANKERA_WALLET:
					case QuestionnaireKey.BANKERA_KYB_2023:
					case QuestionnaireKey.BANKERA_PRIVATE_2023:
						favicon.href = 'bankeraFavicon.png';
						break;
					case QuestionnaireKey.SPECTROCOIN_CORPORATE:
					case QuestionnaireKey.SPECTROCOIN_CORPORATE_BVI:
					case QuestionnaireKey.SPECTROCOIN_EE:
					case QuestionnaireKey.SPECTROCOIN_IBAN:
					case QuestionnaireKey.SPECTROCOIN_IBAN_2023:
					case QuestionnaireKey.SPECTROCOIN_LOANS:
					case QuestionnaireKey.SPECTROCOIN_LT:
					case QuestionnaireKey.SPECTROCOIN_MEDIA:
						favicon.href = 'spectrocoinFavicon.png';
						break;
					default:
						break;
				}
			};

			changeTranslations = async (language: string) => {
				if (!feLanguages[language] && !questionnaireLanguages[language]) {
					return;
				}

				if (typeof feLanguages[language] !== 'function') {
					console.warn(`Failed to load language ${language}`);
					return;
				}
				const feTranslations = await feLanguages[language]();
				const questionnaireTranslations = await questionnaireLanguages[language]();
				this.setState({
					languageData: {
						...feTranslations,
						...questionnaireTranslations
					}
				});
			};

			setInitialLanguage = (language: string, allowedLanguages: string[]) => {
				const urlLanguage = queryString.parse(window.location.search).lang;
				const savedLanguage = isRunningInIFrame()
					? undefined
					: localStorage.getItem('lang');
				const localisedLanguages = [
					'de-DE',
					'pt-BR',
					'pt-PT',
					'zh-CN',
					'zh-TW',
					'es-ES',
					'de-de',
					'pt-br',
					'pt-pt',
					'zh-cn',
					'zh-tw',
					'es-es'
				];
				if (
					urlLanguage &&
					typeof urlLanguage === 'string' &&
					localisedLanguages.includes(urlLanguage) &&
					allowedLanguages.includes(urlLanguage)
				) {
					this.handleLanguageChange(urlLanguage);
				} else if (
					urlLanguage &&
					typeof urlLanguage === 'string' &&
					allowedLanguages.includes(urlLanguage.substr(0, 2).toLowerCase())
				) {
					this.handleLanguageChange(urlLanguage.substr(0, 2).toLowerCase());
				} else if (language && allowedLanguages.includes(language)) {
					this.setLocale(language);
				} else if (savedLanguage && allowedLanguages.includes(savedLanguage)) {
					this.handleLanguageChange(savedLanguage);
				} else if (
					window.navigator.language.substr(0, 2) &&
					allowedLanguages.includes(window.navigator.language.substr(0, 2))
				) {
					this.handleLanguageChange(window.navigator.language.substr(0, 2));
				} else {
					this.handleLanguageChange('en');
				}
			};

			fetchVerification = (sessionKey?: string) =>
				new Promise<void>((resolve, reject) => {
					if (sessionKey) this.sessionKey = sessionKey;
					if (!this.sessionKey) reject(new Error('Session key is missing'));

					Promise.all([
						this.props.axios.get(`/verifications/${this.sessionKey}`),
						this.props.axios.get(`/clients/${this.sessionKey}`),
						this.props.axios.get(`/verifications/${this.sessionKey}/penultimate`)
					])
						.then(
							([
								{ data: verification },
								{ data: client },
								// eslint-disable-next-line object-curly-newline
								{
									data: { questionnaire: previousQuestionnaire }
									// eslint-disable-next-line object-curly-newline
								}
							]: [
								AxiosResponse<FetchedVerification>,
								AxiosResponse<Client>,
								AxiosResponse<{ questionnaire: Questionnaire | null }>
							]) => {
								if (
									client.tooDarkThreshold === null ||
									client.tooBrightThreshold === null
								) {
									throw new Error(`Min or max brightness can not be null`);
								}

								this.setInitialLanguage(
									verification.language,
									client.allowedLanguages
								);

								const stages = sortStages(verification.stages);
								if (!verification.issuingCountry) {
									this.isVerificationWithCountry = false;
								}

								if (!verification.documentType) {
									this.isVerificationWithDocumentType = false;
								}

								if (
									verification.issuingCountry &&
									client.disabledCountries &&
									client.disabledCountries.includes(verification.issuingCountry)
								) {
									throw new Error('UNAVAILABLE COUNTRY');
								}

								this.setMetaTitle(client.key);
								this.setFavicon(client.key);

								// remove contact information from failed blocks if it will not be shown to user
								let questionnaireFailures: string[] = [];
								if (
									verification.questionnaireFailures.includes(
										'CONTACT_INFORMATION_BLOCK'
									) &&
									verification.isEmailProvidedByClient &&
									(client?.key === QuestionnaireKey.BANKERA_WALLET ||
										client?.key === QuestionnaireKey.BANKERA_KYB_2023 ||
										client?.key === QuestionnaireKey.BANKERA_PRIVATE_2023 ||
										client?.key === QuestionnaireKey.FINRAX_BUSINESS ||
										client?.key === QuestionnaireKey.FINRAX_KYB_2023 ||
										client?.key === QuestionnaireKey.PHOENIX_PRIVATE ||
										client?.key === QuestionnaireKey.PHOENIX_CORPORATE ||
										client?.key === QuestionnaireKey.PHOENIX_TIERS ||
										client?.key === QuestionnaireKey.NEOCARD_PRIVATE ||
										client?.key === QuestionnaireKey.NEOCARD_CORPORATE ||
										client?.key === QuestionnaireKey.NEOCARD_TIERS ||
										client?.key === QuestionnaireKey.COLUMIS ||
										client?.key === QuestionnaireKey.TEROXX ||
										client?.key === QuestionnaireKey.TEROXX_CORPORATE ||
										client?.key === QuestionnaireKey.DAW_CORPORATE ||
										client?.key === QuestionnaireKey.BENKER ||
										client?.key === QuestionnaireKey.BENKER_CORPORATE ||
										client?.key === QuestionnaireKey.PPB_KYB ||
										client?.key === QuestionnaireKey.PPB_PRIVATE)
								) {
									questionnaireFailures = verification.questionnaireFailures.filter(
										failure => failure !== 'CONTACT_INFORMATION_BLOCK'
									);
								} else {
									questionnaireFailures = verification.questionnaireFailures;
								}

								const failedBlockValues: any = {};
								verification.questionnaireFailures.forEach(failureKey => {
									verification.questionnaire.forEach((page: any) => {
										page.blocks.forEach((block: any) => {
											if (block.key === failureKey) {
												// this is required for error handling backwards compatibility as old verifications do not have indexes
												failedBlockValues[failureKey] = indexFailedFields(
													block.fields
												);
											}
										});
									});
								});

								const previousFailedBlockValues: any = {};
								if (previousQuestionnaire) {
									verification.questionnaireFailures.forEach(failureKey => {
										previousQuestionnaire.forEach((page: any) => {
											page.blocks.forEach((block: any) => {
												if (block.key === failureKey) {
													// this is required for error handling backwards compatibility as old verifications do not have indexes
													previousFailedBlockValues[
														failureKey
													] = indexFailedFields(block.fields);
												}
											});
										});
									});
								}

								const {
									isNameProvidedByClient,
									isDocumentEditable,
									isEmailProvidedByClient,
									publicNotes,
									...cleanVerification
								} = verification;

								let isNameProvidedInVerification = false;
								if (verification.questionnaire) {
									const { getValueByKey } = getQuestionnaireGetters(
										flattenQuestionnaire(verification.questionnaire)
									);
									isNameProvidedInVerification = !!(
										getValueByKey('CUSTOMER_FIRST_NAME') &&
										getValueByKey('CUSTOMER_LAST_NAME')
									);
								}

								this.setState(
									{
										cachedDateOfBirth: verification.dateOfBirth,
										verification: {
											...cleanVerification,
											dateOfBirth: verification.dateOfBirth
												? new Date(verification.dateOfBirth)
												: null,
											issueDate: verification.issueDate
												? new Date(verification.issueDate)
												: null,
											expirationDate: verification.expirationDate
												? new Date(verification.expirationDate)
												: null,
											supportingDocuments:
												verification.document?.documentTypes,
											stages,
											questionnaireFailures,
											publicNotes
										},
										client: {
											...client,
											disabledCountries: client.disabledCountries || []
										},
										isDocumentEditable,
										isNameEditable:
											!isNameProvidedByClient &&
											!isNameProvidedInVerification,
										isEmailEditable: !isEmailProvidedByClient,
										isPhoneNumberEditable: !verification.phoneNumber,
										failedBlockValues:
											verification.questionnaireStatus ===
											QuestionnaireStatusEnum.STARTED
												? previousFailedBlockValues
												: failedBlockValues,
										wasDocumentUploadAdded: stages.includes(
											Stage.DOCUMENT_UPLOAD
										)
									},
									() => {
										this.loadClientQuestionnaire();
										this.getClientTheme().then(theme => {
											this.themeFile = getThemeWithDefault(theme.default);
											resolve();
										});

										if (this.isBoldDeskEnabled()) {
											this.setBoldDeskWidgetScript();
										}
									}
								);
							}
						)
						.catch((error: AxiosError) => {
							this.locale = window.navigator.language.substr(0, 2) || 'en';
							if (error.response?.data?.key === 'verification_not_accessible') {
								reject(
									new Error(
										`verification_not_accessible ${error.response?.data
											?.defaultMessage || error?.message}`
									)
								);
							}

							reject(
								new Error(
									`Failed to fetch verification: ${error.response?.data
										?.message ||
										error.response?.data?.defaultMessage ||
										error?.message}`
								)
							);
						});
				});

			getClientTheme = (questionnaireKey?: QuestionnaireKey): Promise<any> => {
				if (this.state.client || questionnaireKey) {
					const key = this.state.client?.key || questionnaireKey;
					switch (key) {
						case QuestionnaireKey.BANKERA_WALLET:
						case QuestionnaireKey.BANKERA_KYB_2023:
						case QuestionnaireKey.BANKERA_PRIVATE_2023:
						case QuestionnaireKey.BANKERA_LOANS:
						case QuestionnaireKey.BANKERA_MEDIA:
						case QuestionnaireKey.KVIX:
							return import('../questionnaire/clients/bankeraLoans/theme/theme');
						case QuestionnaireKey.BLACKRIDGE_CM_CORPORATE:
						case QuestionnaireKey.BLACKRIDGE_CM_PRIVATE:
							return import(
								'../questionnaire/clients/blackridgeCorporate/theme/theme'
							);
						case QuestionnaireKey.PERVESK:
						case QuestionnaireKey.CRYPTO_FOREST:
						case QuestionnaireKey.PERVESK_CORPORATE:
						case QuestionnaireKey.PERVESK_MEDIA:
							return import('../questionnaire/clients/pervesk/theme/theme');
						case QuestionnaireKey.SPECTROCOIN_LT:
						case QuestionnaireKey.SPECTROCOIN_EE:
						case QuestionnaireKey.SPECTROCOIN_IBAN:
						case QuestionnaireKey.SPECTROCOIN_IBAN_2023:
						case QuestionnaireKey.SPECTROCOIN_CORPORATE:
						case QuestionnaireKey.SPECTROCOIN_CORPORATE_BVI:
						case QuestionnaireKey.PAXFUL_LOCALBITCOINS:
						case QuestionnaireKey.SPECTROCOIN_MEDIA:
							return import('../questionnaire/clients/spectrocoinLT/theme/theme');
						case QuestionnaireKey.SPECTROCOIN_LOANS:
							return import('../questionnaire/clients/spectrocoinLoans/theme/theme');
						case QuestionnaireKey.VERIFO:
							return import('../questionnaire/clients/verifo/theme/theme');
						case QuestionnaireKey.PPB:
							return import('../questionnaire/clients/PPB/theme/theme');
						case QuestionnaireKey.COLUMIS:
							return import('../questionnaire/clients/columis/theme/theme');
						case QuestionnaireKey.TEROXX:
						case QuestionnaireKey.TEROXX_CORPORATE:
						case QuestionnaireKey.TEROXX_MEDIA:
							return import('../questionnaire/clients/teroxx/theme/theme');
						case QuestionnaireKey.DAW_CORPORATE:
						case QuestionnaireKey.DAW_MEDIA:
							return import('../questionnaire/clients/dawCorporate/theme/theme');
						case QuestionnaireKey.BENKER:
						case QuestionnaireKey.BENKER_CORPORATE:
						case QuestionnaireKey.BENKER_MEDIA:
							return import('../questionnaire/clients/benker/theme/theme');
						default:
							return import('../defaultTheme/defaultTheme');
					}
				}
				return import('../defaultTheme/defaultTheme');
			};

			addRequiredCaptureObjects = () =>
				new Promise<void>(resolve => {
					if (this.state.verification && !this.state.isRequiredCaptureObjectListCreated) {
						let requiredCaptureObjects: CaptureObject[] = [];

						switch (this.state.verification.documentType) {
							case DocumentType.ID_CARD: {
								requiredCaptureObjects = [
									CaptureObject.ID_FRONT,
									CaptureObject.ID_BACK,
									CaptureObject.SELFIE
								];
								break;
							}
							case DocumentType.PASSPORT: {
								requiredCaptureObjects = [
									CaptureObject.PASSPORT_PAGE,
									CaptureObject.SELFIE
								];
								break;
							}
							case DocumentType.RESIDENCE_PERMIT: {
								requiredCaptureObjects = [
									CaptureObject.RESIDENCE_PERMIT_FRONT,
									CaptureObject.RESIDENCE_PERMIT_BACK,
									CaptureObject.SELFIE
								];
								break;
							}
							case DocumentType.DRIVERS_LICENCE: {
								requiredCaptureObjects = [
									CaptureObject.DRIVERS_LICENCE_FRONT,
									CaptureObject.DRIVERS_LICENCE_BACK,
									CaptureObject.SELFIE
								];
								break;
							}
							default: {
								break;
							}
						}
						this.videoCaptureObjects = requiredCaptureObjects;

						this.setState(
							{
								requiredCaptureObjects,
								isRequiredCaptureObjectListCreated: true
							},
							resolve
						);
					}
				});

			getVerificationFromState = () =>
				this.state.verification && {
					email: this.state.verification.email,
					phoneNumber: this.state.verification.phoneNumber,
					firstName: this.state.verification.firstName,
					middleName: this.state.verification.middleName,
					lastName: this.state.verification.lastName,
					companyName: this.state.verification.companyName,
					personalCode: this.state.verification.personalCode,
					dateOfBirth: this.state.verification.dateOfBirth
						? dateToShortDate(this.state.verification.dateOfBirth)
						: this.state.cachedDateOfBirth,
					nationality: this.state.verification.nationality,
					issuingCountry: this.state.verification.issuingCountry,
					documentNumber: this.state.verification.documentNumber,
					documentType: this.state.verification.documentType,
					citizenship: this.state.verification.citizenship,
					issueDate: dateToShortDate(this.state.verification.issueDate),
					expirationDate: dateToShortDate(this.state.verification.expirationDate),
					questionnaire: this.state.verification.questionnaire,
					isQuestionnaireComplete: this.state.isQuestionnaireComplete,
					language: this.locale
				};

			getDocumentInformationFromState = () =>
				this.state.verification && {
					documentType: this.state.verification.documentType,
					issuingCountry: this.state.verification.issuingCountry,
					citizenship: this.state.verification.citizenship,
					documentNumber: this.state.verification.documentNumber,
					issueDate: dateToShortDate(this.state.verification.issueDate),
					expirationDate: dateToShortDate(this.state.verification.expirationDate),
					nationality: this.state.verification.nationality
				};

			checkForAdditionalQuestions = (openLoadingPage?: boolean) => {
				if (this.state.client?.isQuestionnairePrefillEnabled) {
					return new Promise<void>(resolve =>
						this.props.axios
							.get(`/additional-questions/questions`, {
								params: {
									sessionToken: this.sessionKey,
									allRelevant:
										this.state.verification?.questionnaireStatus !==
										QuestionnaireStatusEnum.SUBMITTED
								}
							})
							.then(({ data: response }: AxiosResponse<AdditionalQuestion[]>) => {
								if (response.length) {
									if (openLoadingPage) {
										this.openPage(Page.LOADING);
									}

									const isNewQuestion = response.some(
										singleQuestion =>
											singleQuestion.status === QuestionStatusEnum.NEW
									);

									this.setState({
										areAdditionalQuestionsAdded: true,
										shouldAQsBeRecheckedForPrefillClient: isNewQuestion,
										isNewAdditionalQuestionsPresent: isNewQuestion
									});
								}
							})
							.catch(() => {
								this.setState({ areAdditionalQuestionsAdded: true });
							})
							.then(() => resolve())
					);
				}
				return new Promise<void>(resolve =>
					this.props.axios
						.get(`/additional-questions/questions`, {
							params: { sessionToken: this.sessionKey }
						})
						.then(({ data: response }: AxiosResponse<any>) => {
							if (response.length) {
								if (openLoadingPage) {
									this.openPage(Page.LOADING);
								}
								response.forEach((additionalQuestion: any) => {
									if (
										additionalQuestion.status === QuestionStatusEnum.NEW ||
										additionalQuestion.status === QuestionStatusEnum.FAILED
									) {
										this.setState({ areAdditionalQuestionsAdded: true });
									}
								});
							}
						})
						.catch(() => {
							this.setState({ areAdditionalQuestionsAdded: true });
						})
						.then(() => resolve())
				);
			};

			handleDoneClick = () => {
				// Inform mobile app about successfuly submitted verification
				if (window?.handler?.onVerificationSuccess) {
					window.handler.onVerificationSuccess();
				} else if (
					(this.startFromApp || this.continueFromApp) &&
					window.ReactNativeWebView
				) {
					window.ReactNativeWebView.postMessage(
						JSON.stringify({
							eventId: WebViewEventIdEnum.VERIFICATION_SUCCESS
						})
					);
				} else if (isRunningInIFrame()) {
					// Post message to parent window, if running in an iframe
					window.parent.postMessage('success', '*');
					window.parent.postMessage({ event: 'success' }, '*');
				} else if (this.state.client) {
					if (this.state.client.successUrl.includes('?')) {
						window.location.assign(
							`${this.state.client.successUrl}&lang=${this.locale}`
						);
					} else {
						window.location.assign(
							`${this.state.client.successUrl}?lang=${this.locale}`
						);
					}
				}
			};

			handleFailClick = () => {
				if (this.state.client) {
					if (this.state.client.failureUrl.includes('?')) {
						window.location.assign(
							`${this.state.client.failureUrl}&lang=${this.locale}`
						);
					} else {
						window.location.assign(
							`${this.state.client.failureUrl}?lang=${this.locale}`
						);
					}
				}
			};

			handleUnavailableNextClick = () => {
				this.openPage(Page.QR);
			};

			openPermissionsOrAgreement = () => {
				if (this.state.isAgreementEnabled) {
					this.openPage(Page.AGREEMENT);
				} else if (
					this.state.verification?.stages.includes(Stage.MEDIA) &&
					this.isPermissionsPageEnabled
				) {
					this.openPage(Page.PERMISSIONS);
				} else {
					this.handlePermissionsNextClick(false);
				}
			};

			handlePermissionsNextClick = (skipIncreaseProgress: boolean) => {
				this.attachPageBlocker();
				if (
					this.isMediaOnly() ||
					(this.state.isMediaBeforeQuestionnaire && this.isMediaAndQuestionnaire())
				) {
					if (
						this.state.hasCameraPermission ||
						window.handler?.onNativeCameraRequested ||
						this.continueFromApp ||
						this.startFromApp
					) {
						if (!skipIncreaseProgress) {
							this.increaseProgress();
						}
						if (this.state.client?.isQuestionnairePrefillEnabled) {
							return this.openPage(Page.VERIFICATION_HANDOVER);
						}
						return this.openPage(Page.INTERIM_QR);
					}
					if (this.isMediaOnly()) {
						return this.openDocumentUploadOrQR();
					}
					return this.openPage(Page.INTERIM_QR);
				}
				if (
					this.isQuestionnaireOnly() ||
					(this.isMediaAndQuestionnaire() && !this.state.isMediaBeforeQuestionnaire)
				) {
					if (!skipIncreaseProgress) {
						this.increaseProgress();
					}
					return this.openPage(Page.QUESTIONNAIRE);
				}
				if (this.state.areAdditionalQuestionsAdded) {
					return this.openPage(Page.ADDITIONAL_QUESTIONS);
				}
				if (
					this.state.verification?.stages.length === 1 &&
					this.state.verification.stages.includes(Stage.DOCUMENT_UPLOAD)
				) {
					return this.openPage(Page.DOCUMENT_UPLOAD);
				}
				return null;
			};

			handleAgreementNextClick = () => {
				if (
					this.state.hasUserAgreement ||
					this.agreementComponent === this.secondAgreementComponent
				) {
					this.increaseProgress().then(() => {
						this.openPage(Page.LOADING);
						this.completeVerification();
					});
				} else {
					this.attachPageBlocker();
					this.setState(
						{
							hasUserAgreement: true
						},
						() => {
							this.increaseProgress().then(() => {
								if (!this.isQuestionnaireOnly()) {
									this.openPage(Page.PERMISSIONS);
								} else {
									this.handlePermissionsNextClick(true);
								}
							});
						}
					);
				}
			};

			handleIntroNextClick = () => {
				if (this.isIntroPageEnabled) {
					this.increaseProgress();
				}
				if (this.state.client?.isQuestionnairePrefillEnabled && !this.isQuestionnaire()) {
					if (this.state.areAdditionalQuestionsAdded) {
						return this.openPage(Page.ADDITIONAL_QUESTIONS);
					}
					if (this.state.verification?.stages.includes(Stage.DOCUMENT_UPLOAD)) {
						return this.openPage(Page.DOCUMENT_UPLOAD);
					}
				}
				if (
					this.isMediaOnly() ||
					(this.state.isMediaBeforeQuestionnaire && this.isMediaAndQuestionnaire())
				) {
					// @ts-ignore
					if (window.handler?.onNativeCameraRequested || this.startFromApp) {
						this.increaseProgress();
						return this.openPermissionsOrAgreement();
					}
					if (this.state.hasCamera) {
						return this.requestCameraPermission()
							.then(this.openPermissionsOrAgreement)
							.catch(() => this.openDocumentUploadOrQR());
					}
					return this.openPermissionsOrAgreement();
				}
				return this.openPermissionsOrAgreement();
			};

			handleInterimQRNextClick = () => {
				if (this.isMediaAndQuestionnaire() && this.state.isMediaBeforeQuestionnaire) {
					if (
						this.state.hasCameraPermission ||
						window.handler?.onNativeCameraRequested ||
						this.startFromApp
					) {
						return this.openPage(Page.MEDIA);
					}

					return this.openPage(Page.QUESTIONNAIRE);
				}
				if (this.state.verification?.stages.includes(Stage.MEDIA)) {
					return this.openPage(Page.MEDIA);
				}
				if (this.state.areAdditionalQuestionsAdded) {
					return this.openPage(Page.ADDITIONAL_QUESTIONS);
				}
				return this.openPage(Page.MEDIA);
			};

			handleAdditionalQuestionsNextClick = async () => {
				this.setState({
					areAdditionalQuestionsAdded: false,
					isNewAdditionalQuestionsPresent: false
				});
				if (this.state.wasDocumentUploadAdded) {
					return this.openPage(Page.DOCUMENT_UPLOAD);
				}
				if (this.state.client?.isQuestionnairePrefillEnabled) {
					return this.openPage(Page.VERIFICATION_HANDOVER);
				}
				if (!this.isQuestionnaire()) {
					return this.openPage(Page.CONFIRMATION);
				}
				return this.completeVerification();
			};

			handleDocumentUploadNextClick = () => {
				if (this.state.wasDocumentUploadAdded) {
					this.setState(
						{
							wasDocumentUploadAdded: false
						},
						() => {
							if (this.isMedia()) {
								if (this.state.hasCameraPermission) {
									if (this.state.isSecondAgreementEnabled) {
										this.agreementComponent = this.secondAgreementComponent;
										return this.openPage(Page.AGREEMENT);
									}
									return this.increaseProgress().then(() => {
										this.openPage(Page.LOADING);
										this.completeVerification();
									});
								}
								if (
									this.state.client?.isQuestionnairePrefillEnabled &&
									!this.state.isMediaComplete
								) {
									return this.openPage(Page.VERIFICATION_HANDOVER);
								}
								if (
									this.state.client?.isQuestionnairePrefillEnabled &&
									this.state.isMediaComplete
								) {
									return this.openPage(Page.CONFIRMATION);
								}
								if (!this.state.isMediaComplete) {
									return this.requestCameraPermission()
										.then(() => this.openPage(Page.INTERIM_QR))
										.catch(() => this.openPage(Page.QR));
								}
							}
							if (this.state.isSecondAgreementEnabled) {
								this.agreementComponent = this.secondAgreementComponent;
								return this.openPage(Page.AGREEMENT);
							}
							if (
								this.state.client?.isQuestionnairePrefillEnabled &&
								!this.state.isMediaComplete
							) {
								return this.openPage(Page.VERIFICATION_HANDOVER);
							}
							if (
								this.state.client?.isQuestionnairePrefillEnabled &&
								this.state.isMediaComplete
							) {
								return this.completeDocumentUpload().then(() => {
									this.openPage(Page.LOADING);
									this.completeVerification();
								});
							}
							if (!this.isQuestionnaire()) {
								return this.openPage(Page.CONFIRMATION);
							}
							return this.completeVerification();
						}
					);
				}
			};

			completeDocumentUpload = () =>
				this.props.axios.post(
					`/verifications/${this.sessionKey}/supporting-documents/complete`
				);

			handleQuestionnaireSubmitOnConfirmation = () => {
				this.setState({ isQuestionnaireUpdating: true });
				this.increaseProgress();
				this.updateCompletedQuestionnaireStages();
				if (this.state.areAdditionalQuestionsAdded) {
					this.openedPage.current = Page.ADDITIONAL_QUESTIONS;
					return this.openPage(Page.ADDITIONAL_QUESTIONS);
				}
				if (this.state.wasDocumentUploadAdded) {
					this.openedPage.current = Page.DOCUMENT_UPLOAD;
					return this.openPage(Page.DOCUMENT_UPLOAD);
				}
				this.openedPage.current = Page.VERIFICATION_HANDOVER;
				return this.openPage(Page.VERIFICATION_HANDOVER);
			};

			handleQuestionnaireSubmit = (
				questionnaire: Questionnaire,
				information: Information,
				isComplete = false
			): Promise<void> => {
				return new Promise((resolve, reject) =>
					this.setState(
						prevState => {
							return {
								isQuestionnaireComplete:
									isComplete && !prevState.client?.isQuestionnairePrefillEnabled,
								verification: {
									...(prevState.verification as Verification),
									...information,
									questionnaire
								}
							};
						},
						() => {
							this.submitVerification()
								.then(() => {
									if (isComplete && this.state.verification) {
										this.setState({ isQuestionnaireUpdating: true });
										this.increaseProgress();
										this.updateCompletedQuestionnaireStages();
										// Additional questionnaire stages and media complete, finish verification
										if (this.state.isMediaComplete && isComplete) {
											return this.completeVerification();
										}

										// KYC has only questionnaire
										if (
											!this.state.verification.stages.includes(Stage.MEDIA) &&
											this.state.verification.stages.includes(
												Stage.DOCUMENT_UPLOAD
											)
										) {
											if (this.state.isSecondAgreementEnabled) {
												this.agreementComponent = this.secondAgreementComponent;
												return this.openPage(Page.AGREEMENT);
											}
											if (this.state.areAdditionalQuestionsAdded) {
												return this.openPage(Page.ADDITIONAL_QUESTIONS);
											}
											return this.openPage(Page.DOCUMENT_UPLOAD);
										}
										if (
											!this.state.verification.stages.includes(Stage.MEDIA) &&
											!this.state.verification.stages.includes(
												Stage.DOCUMENT_UPLOAD
											)
										) {
											if (this.state.isSecondAgreementEnabled) {
												this.agreementComponent = this.secondAgreementComponent;
												return this.openPage(Page.AGREEMENT);
											}
											if (this.state.areAdditionalQuestionsAdded) {
												return this.openPage(Page.ADDITIONAL_QUESTIONS);
											}
											return this.logUserEvent(
												UserEvent.CONFIRMATION_PAGE
											).then(() => this.completeVerification());
										}

										// KYC has both media and questionnaire, media is already completed as it is before questionnaire
										if (
											this.state.isMediaBeforeQuestionnaire &&
											this.state.verification.stages.includes(Stage.MEDIA)
										) {
											if (this.state.hasCameraPermission) {
												if (this.state.isSecondAgreementEnabled) {
													this.agreementComponent = this.secondAgreementComponent;
													return this.openPage(Page.AGREEMENT);
												}
												if (this.state.areAdditionalQuestionsAdded) {
													return this.openPage(Page.ADDITIONAL_QUESTIONS);
												}
												if (
													this.state.isQuestionnaireUpdating ||
													this.uploadQueue.length ||
													this.state.isWaitingForVideo ||
													(this.state.requiredCaptureObjects &&
														this.state.requiredCaptureObjects.length)
												) {
													this.openPage(Page.LOADING);
													return Promise.resolve();
												}
												return this.logUserEvent(
													UserEvent.CONFIRMATION_PAGE
												).then(() => this.completeVerification());
											}
											return this.openDocumentUploadOrQR();
										}

										// KYC has both media and questionnaire, prefill enabled continue to precalc added stages
										if (this.state.client?.isQuestionnairePrefillEnabled) {
											if (this.state.areAdditionalQuestionsAdded) {
												if (
													this.openedPage.current ===
													Page.ADDITIONAL_QUESTIONS
												) {
													this.openedPage.current = null;
													this.setState({
														isQuestionnaireUpdating: false
													});
													return null;
												}
												return this.openPage(Page.ADDITIONAL_QUESTIONS);
											}
											if (this.state.wasDocumentUploadAdded) {
												if (
													this.openedPage.current === Page.DOCUMENT_UPLOAD
												) {
													this.openedPage.current = null;
													this.setState({
														isQuestionnaireUpdating: false
													});
													return null;
												}
												return this.openPage(Page.DOCUMENT_UPLOAD);
											}
											if (
												this.openedPage.current ===
												Page.VERIFICATION_HANDOVER
											) {
												this.openedPage.current = null;
												this.setState({ isQuestionnaireUpdating: false });
												return null;
											}
											return this.openPage(Page.VERIFICATION_HANDOVER);
										}

										// KYC has both media and questionnaire, continue to media
										if (
											!this.state.isMediaBeforeQuestionnaire &&
											this.isMedia()
										) {
											this.requestCameraPermission()
												.then(() => this.openPage(Page.INTERIM_QR))
												.catch(() => this.openDocumentUploadOrQR());
										}
									}
									return resolve();
								})
								.catch(() => {
									reject();
								});
						}
					)
				);
			};

			handlePrefillNextClick = () => {
				return this.openPage(Page.CONFIRMATION);
			};

			handleConfirmationNextClick = () => {
				this.setState(
					{
						isQuestionnaireComplete: true,
						isQuestionnaireUpdating: true
					},
					() => {
						if (
							this.state.verification?.stages.includes(Stage.DOCUMENT_UPLOAD) &&
							this.state.client?.isQuestionnairePrefillEnabled
						) {
							this.completeDocumentUpload();
						}

						if (this.isQuestionnaire()) {
							return this.submitVerification().then(() => {
								if (this.isMedia() && !this.state.isMediaComplete) {
									if (
										this.state.hasCameraPermission ||
										window.handler?.onNativeCameraRequested ||
										this.startFromApp
									) {
										return this.openPage(Page.INTERIM_QR);
									}
									if (!this.state.hasCameraPermission) {
										return this.requestCameraPermission()
											.then(() => this.openPage(Page.INTERIM_QR))
											.catch(() => this.openPage(Page.QR));
									}
									return this.openPage(Page.QR);
								}
								return this.completeVerification();
							});
						}

						// Don't submit questionnaire if it is not present
						if (this.isMedia() && !this.state.isMediaComplete) {
							if (
								this.state.hasCameraPermission ||
								window.handler?.onNativeCameraRequested ||
								this.startFromApp
							) {
								return this.openPage(Page.INTERIM_QR);
							}
							if (!this.state.hasCameraPermission) {
								return this.requestCameraPermission()
									.then(() => this.openPage(Page.INTERIM_QR))
									.catch(() => this.openPage(Page.QR));
							}
							return this.openPage(Page.QR);
						}
						return this.completeVerification();
					}
				);
			};

			handlePrefillForward = async () => {
				this.setState({ shouldShowHandoverUrl: true });
				this.openPage(Page.SUCCESS);
			};

			openDocumentUploadOrQR = () => {
				if (this.state.areAdditionalQuestionsAdded) {
					return this.openPage(Page.ADDITIONAL_QUESTIONS);
				}

				if (this.state.wasDocumentUploadAdded) {
					return this.openPage(Page.DOCUMENT_UPLOAD);
				}
				return this.openPage(Page.QR);
			};

			promptNextDocument = () => {
				if (this.state.requiredCaptureObjects) {
					const requiredCaptureObjects: CaptureObject[] = [
						...this.state.requiredCaptureObjects
					];

					const activeCaptureObject = requiredCaptureObjects.shift();
					if (activeCaptureObject !== undefined) {
						if (
							this.state.hasMultipleCameras &&
							activeCaptureObject === CaptureObject.SELFIE
						) {
							this.requestCameraPermission('user');
						} else {
							this.requestCameraPermission();
						}
						this.setState(
							{
								activeCaptureObject,
								requiredCaptureObjects
							},
							() => this.openPage(Page.MEDIA)
						);
					} else {
						this.setState(
							{
								activeCaptureObject: null
							},
							() => {
								this.stopRecording();
								if (
									this.isMediaAndQuestionnaire() &&
									this.state.isMediaBeforeQuestionnaire
								) {
									this.openPage(Page.QUESTIONNAIRE);
								} else if (this.state.isSecondAgreementEnabled) {
									this.agreementComponent = this.secondAgreementComponent;
									this.openPage(Page.AGREEMENT);
								} else {
									this.openPage(Page.LOADING);
								}
							}
						);
					}
				}
			};

			openCameraPage = () => this.addRequiredCaptureObjects().then(this.promptNextDocument);

			completeVerification = async (isOldKYCApp = false) => {
				await this.checkForAdditionalQuestions(true);

				const returnStatement = () =>
					this.props.axios
						.post(`/verifications/${this.sessionKey}/complete`, null, {
							responseType: 'text',
							transformResponse: [
								data => {
									try {
										return JSON.parse(data);
									} catch (err) {
										return undefined;
									}
								}
							] // parse to json only if data is present
						})
						.then(async () => {
							this.detachPageBlocker();
							this.increaseProgress();
							const shouldApplicationPaymentBeVisible = await this.getPaymentInfo();
							if (shouldApplicationPaymentBeVisible) {
								this.openPage(Page.APPLICATION_PAYMENT);
							} else {
								this.openPage(Page.SUCCESS);
							}
						})
						.catch(error => {
							const errorCode =
								error.response?.data?.errorCode || error?.errorCode || '';
							const errorMessage =
								error.response?.data?.defaultMessage || error?.message || '';

							if (
								(this.startFromApp || this.continueFromApp) &&
								window.ReactNativeWebView
							) {
								window.ReactNativeWebView.postMessage(
									JSON.stringify({
										eventId: WebViewEventIdEnum.VERIFICATION_FAILURE,
										data: {
											errorCode,
											errorMessage
										}
									})
								);
							} else if (window.handler?.onVerificationFailure) {
								window.handler.onVerificationFailure(errorCode, errorMessage);
							}
							this.fail(
								'Failed to complete verification',
								VerificationFailureReason.FAILED_COMPLETE
							);
						});

				const shouldAQsBeRechecked =
					(this.state.client?.isQuestionnairePrefillEnabled &&
						this.state.shouldAQsBeRecheckedForPrefillClient) ||
					!this.state.client?.isQuestionnairePrefillEnabled;
				if (
					this.state.areAdditionalQuestionsAdded &&
					shouldAQsBeRechecked &&
					(!this.state.isMediaComplete ||
						!this.state.client?.isQuestionnairePrefillEnabled ||
						this.state.isNewAdditionalQuestionsPresent)
				) {
					return this.openPage(Page.ADDITIONAL_QUESTIONS);
				}
				if (this.state.wasDocumentUploadAdded) {
					return this.openPage(Page.DOCUMENT_UPLOAD);
				}
				if (
					this.state.wasQuestionnaireStagesAdded &&
					this.state.verification?.stages.filter(
						stage => stage !== Stage.DOCUMENT_UPLOAD && stage !== Stage.MEDIA
					).length &&
					!this.state.client?.isQuestionnairePrefillEnabled
				) {
					this.setState({
						wasQuestionnaireStagesAdded: false
					});
					return this.openPage(Page.QUESTIONNAIRE);
				}
				if (this.state.isVerificationComplete) {
					return returnStatement();
				}
				this.isVerificationComplete = true;
				this.stopRecording();
				if (
					this.state.client?.isQuestionnairePrefillEnabled &&
					!this.state.isQuestionnaireComplete &&
					(!this.state.isMediaComplete || !this.isMedia())
				) {
					return this.openPage(Page.VERIFICATION_HANDOVER);
				}

				if (this.startFromApp || this.continueFromApp) {
					return returnStatement();
				}

				if (window?.handler?.isKYCApp) {
					// if not android, check if it's iOS kyc app
					return window.handler.isKYCApp.then((isKYCApp: boolean) => {
						this.isKYCApp = isKYCApp;
						returnStatement();
					});
				}
				if (window?.handler?.isKYCAppAndroid) {
					this.isKYCApp = window.handler.isKYCAppAndroid();
					return returnStatement();
				}
				if (isOldKYCApp) {
					this.isKYCApp = isOldKYCApp;
					return returnStatement();
				}
				return returnStatement();
			};

			getTotalSteps = () => {
				// Success, Loading, UserPermissions
				let totalSteps = 3;

				if (!this.isIntroPageEnabled) {
					totalSteps -= 1;
				}

				if (this.state.isAgreementEnabled) {
					totalSteps += 1;
				}

				if (this.state.isSecondAgreementEnabled) {
					totalSteps += 1;
				}

				if (this.state.isMediaBeforeQuestionnaire) {
					// If media is before questionnaire we get two pages (Country and Document select)
					if (!this.isVerificationWithCountry) {
						totalSteps += 1;
					}

					if (!this.isVerificationWithDocumentType) {
						totalSteps += 1;
					}
				}

				if (this.isQuestionnaireOnly()) {
					totalSteps += this.state.questionnaireLength;
					// Questionnaire only never goes to Loading
					totalSteps -= 1;
				} else {
					if (this.isMedia()) {
						// Pasport only has 2 photos which are 4 steps
						if (
							this.state.verification?.documentType?.includes(DocumentType.PASSPORT)
						) {
							totalSteps += 4;
						} else {
							totalSteps += 6;
						}
					}
					if (this.state.verification?.stages.includes(Stage.DOCUMENT_UPLOAD)) {
						totalSteps += 1;
					}
					if (this.isQuestionnaire()) {
						totalSteps += this.state.questionnaireLength;
					}
				}

				return totalSteps;
			};

			increaseProgress = (step?: number): Promise<void> => {
				return new Promise(resolve => {
					if (
						this.state.progressBarPosition < this.getTotalSteps() ||
						this.state.progressBarPosition < this.appTotalSteps
					) {
						return this.setState(
							prevState => ({
								progressBarPosition: prevState.progressBarPosition + (step || 1)
							}),
							resolve
						);
					}
					return resolve();
				});
			};

			decreaseProgress = () => {
				if (this.state.progressBarPosition > 0) {
					this.setState(prevState => ({
						progressBarPosition: prevState.progressBarPosition - 1
					}));
				}
			};

			getIsVerificationComplete = () => this.isVerificationComplete;

			logUserEvent = (userEvent: UserEvent): Promise<void> => {
				if (this.isVerificationComplete) {
					return new Promise<void>(resolve => {
						resolve();
					});
				}
				return new Promise<void>((resolve, reject) => {
					if (this.sessionKey) {
						this.props.axios
							.post(
								`/verifications/${this.sessionKey}/user-events`,
								{
									eventType: userEvent
								},
								{
									responseType: 'text',
									transformResponse: [
										data => {
											try {
												return JSON.parse(data);
											} catch (err) {
												return undefined;
											}
										}
									] // parse to json only if data is present
								}
							)
							.then(() => {
								resolve();
							})
							// @ts-ignore
							.catch((error: AxiosError) => {
								const notAccessible =
									error?.response?.data?.key === 'verification_not_accessible';
								if (notAccessible || userEvent === UserEvent.SESSION_STARTED) {
									this.locale = window.navigator.language.substr(0, 2) || 'en';
									if (!window.isRedirecting) {
										this.fail(
											`${
												notAccessible ? `${error.response?.data?.key} ` : ''
											}${error.response?.data?.defaultMessage}`,
											VerificationFailureReason.AUTHENTICATION
										);
									}
									reject();
								}
								resolve();
							});
					} else {
						resolve();
					}
				});
			};

			blockPageNavigation = (event: Event) => {
				event.preventDefault();
				return '';
			};

			attachPageBlocker = () => {
				if (process.env.NODE_ENV !== 'development') {
					window.addEventListener('beforeunload', this.blockPageNavigation);
				}
			};

			detachPageBlocker = () =>
				window.removeEventListener('beforeunload', this.blockPageNavigation);

			// Dont check for document upload stage for flows as it is checked at the very end only
			isQuestionnaire = () =>
				!!(
					this.state.verification &&
					this.state.verification.stages.filter(
						stage => stage !== Stage.MEDIA && stage !== Stage.DOCUMENT_UPLOAD
					).length
				);

			isQuestionnaireOnly = () =>
				!!(
					this.state.verification &&
					this.isQuestionnaire() &&
					!this.state.verification.stages.includes(Stage.MEDIA)
				);

			isMedia = () =>
				!!(this.state.verification && this.state.verification.stages.includes(Stage.MEDIA));

			isMediaOnly = () =>
				!!(
					this.state.verification &&
					((this.state.verification.stages.length === 1 &&
						this.state.verification.stages.includes(Stage.MEDIA)) ||
						(this.state.verification.stages.length === 2 &&
							this.state.verification.stages.includes(Stage.MEDIA) &&
							this.state.verification.stages.includes(Stage.DOCUMENT_UPLOAD)))
				);

			isMediaAndQuestionnaire = () => {
				return (
					this.state.verification &&
					this.state.verification.stages.includes(Stage.MEDIA) &&
					((this.state.verification.stages.includes(Stage.DOCUMENT_UPLOAD) &&
						this.state.verification.stages.length > 2) ||
						(!this.state.verification.stages.includes(Stage.DOCUMENT_UPLOAD) &&
							this.state.verification.stages.length > 1))
				);
			};

			isRetryingQuestionnaire = () =>
				!!(this.state.verification && this.state.verification.questionnaireFailures.length);

			getDocumentInformation: () => DocumentInformation = () => {
				let firstName: string | null = null;
				let middleName: string | null = null;
				let lastName: string | null = null;
				let companyName: string | null = null;
				let personalCode: string | null = null;
				let nationality: string | null = null;
				let issuingCountry: string | null = null;
				let documentNumber: string | null = null;
				let documentType: DocumentType | null = null;
				let dateOfBirth: Date | null = null;
				let issueDate: Date | null = null;
				let expirationDate: Date | null = null;
				let citizenship: string | null = null;

				if (this.state.verification) {
					({
						firstName,
						middleName,
						lastName,
						companyName,
						personalCode,
						nationality,
						issuingCountry,
						documentNumber,
						documentType,
						dateOfBirth,
						issueDate,
						expirationDate,
						citizenship
					} = this.state.verification);
				}
				if (this.state.verification?.questionnaire) {
					const { getValueByKey } = getQuestionnaireGetters(
						flattenQuestionnaire(this.state.verification?.questionnaire)
					);
					if (!firstName) {
						firstName = getValueByKey('CUSTOMER_FIRST_NAME');
					}
					if (!middleName) {
						middleName = getValueByKey('CUSTOMER_MIDDLE_NAME');
					}
					if (!lastName) {
						lastName = getValueByKey('CUSTOMER_LAST_NAME');
					}
					if (!companyName) {
						companyName =
							getValueByKey('FULL_LEGAL_NAME') ||
							getValueByKey('COMPANY_NAME') ||
							null;
					}
				}
				return {
					firstName,
					middleName,
					lastName,
					companyName,
					personalCode,
					nationality,
					issuingCountry,
					documentNumber,
					documentType,
					dateOfBirth,
					issueDate,
					expirationDate,
					citizenship
				};
			};

			getContactInformation: () => ContactInformation = () => {
				let phoneNumber: string | null = null;
				let email: string | null = null;

				if (this.state.verification) {
					({ phoneNumber, email } = this.state.verification);
				}

				return {
					phoneNumber,
					email
				};
			};

			registerResizeCallback = () => {
				const rootElement = document.body;
				if (rootElement && isRunningInIFrame()) {
					registerHeightObserver(rootElement, () => {
						if (rootElement.scrollHeight) {
							// Post message to parent window, if running in an iframe
							window.parent.postMessage(
								{ event: 'resize', height: rootElement.scrollHeight },
								'*'
							);
						}
					});
				}
			};

			registerAppInterface = () => {
				window.kicHandler = {
					onVideoCaptured: (base64Video: string, captureObject: CaptureObject) => {
						this.cameraName = this.cameraName || 'Mobile app';
						if (!base64Video && captureObject) {
							this.uploadedFiles.video.push(captureObject);
							if (
								this.uploadedFiles.video.includes(captureObject) &&
								this.uploadedFiles.image.includes(captureObject) &&
								window.handler?.onMediaUploadSuccess
							) {
								window.handler.onMediaUploadSuccess();
							}
							this.isUploading = false;
							if (this.uploadQueue.length) {
								this.triggerUpload();
							} else if (
								this.state.activePage === Page.LOADING &&
								(!this.state.requiredCaptureObjects ||
									!this.state.requiredCaptureObjects.length)
							) {
								if (this.state.isSecondAgreementEnabled) {
									this.agreementComponent = this.secondAgreementComponent;
									this.openPage(Page.AGREEMENT);
								} else {
									this.completeVerification();
								}
							}
						} else {
							// convert base64/URLEncoded data component to raw binary data held in a string
							let byteString;
							if (base64Video.split(',')[0].indexOf('base64') >= 0) {
								byteString = atob(base64Video.split(',')[1]);
							} else {
								byteString = unescape(base64Video.split(',')[1]);
							}

							// separate out the mime component
							const mimeString = base64Video
								.split(',')[0]
								.split(':')[1]
								.split(';')[0];
							let fileType = mimeString.split('/')[1];
							if (!fileType) {
								if (mimeString.includes('mp4')) {
									fileType = 'mp4';
								} else if (mimeString.includes('webm')) {
									fileType = 'webm';
								}
							}

							// write the bytes of the string to a typed array
							const ia = new Uint8Array(byteString.length);
							for (let i = 0; i < byteString.length; i++) {
								ia[i] = byteString.charCodeAt(i);
							}

							const videoBlob = new Blob([ia], { type: mimeString });
							this.saveVideo(
								captureObject,
								videoBlob,
								`documentCaptureLivePhoto.${fileType}`
							);
						}
					},
					onImageCaptured: (
						base64Image: string,
						captureObject: CaptureObject,
						deviceName = ''
					) => {
						this.cameraName = deviceName || 'Mobile app';
						// convert base64/URLEncoded data component to raw binary data held in a string
						let byteString;
						if (base64Image.split(',')[0].indexOf('base64') >= 0) {
							byteString = atob(base64Image.split(',')[1]);
						} else {
							byteString = unescape(base64Image.split(',')[1]);
						}

						// separate out the mime component
						const mimeString = base64Image
							.split(',')[0]
							.split(':')[1]
							.split(';')[0];

						// write the bytes of the string to a typed array
						const ia = new Uint8Array(byteString.length);
						for (let i = 0; i < byteString.length; i++) {
							ia[i] = byteString.charCodeAt(i);
						}

						const imageBlob = new Blob([ia], { type: mimeString });
						this.saveImage(captureObject, imageBlob);
					},
					cameraCompleted: (
						totalSteps: number,
						currentProgress: number,
						isKYCApp = false
					) => {
						if (isKYCApp) {
							this.isKYCApp = isKYCApp;
						}
						this.isComingFromApp = true;
						this.appTotalSteps = totalSteps;
						this.increaseProgress(currentProgress + 1);

						this.setState({ hasCameraPermission: true }, () => {
							if (this.state.client?.isQuestionnairePrefillEnabled) {
								return this.setState({ isVerificationComplete: true }, () =>
									this.completeVerification(isKYCApp)
								);
							}

							if (this.state.isMediaBeforeQuestionnaire && this.isQuestionnaire()) {
								return this.openPage(Page.QUESTIONNAIRE);
							}
							if (this.state.isSecondAgreementEnabled) {
								this.agreementComponent = this.secondAgreementComponent;
								return this.openPage(Page.AGREEMENT);
							}
							this.openPage(Page.LOADING);
							return this.completeVerification(isKYCApp);
						});
					}
				};
			};

			unregisterResizeCallback = () => {
				if (isRunningInIFrame()) {
					unregisterHeightObserver(window.document.body);
				}
			};

			/**
			 * Apply styling to root element when it is not running in an iframe
			 */
			patchRootStyling = () => {
				const rootElement = document.getElementById('root');
				if (!isRunningInIFrame() && rootElement) {
					// Running in it's own window
					rootElement.style.minHeight = '100vh';
				}
			};

			handlePageChange = () => {
				if (isRunningInIFrame()) {
					window.parent.postMessage({ event: 'page change' }, '*');
				}
			};

			handleCountrySelect = (issuingCountry: string) =>
				new Promise<void>(resolve => {
					this.increaseProgress();
					// Update data in questionnaire as questionaire wont be rendered for it to update its values
					const questionnaire = this.state.verification?.questionnaire;
					if (questionnaire) {
						switch (this.state?.client?.key) {
							case QuestionnaireKey.SPECTROCOIN_LOANS:
							case QuestionnaireKey.BANKERA_LOANS:
								questionnaire[0].blocks[7].fields[1].value = issuingCountry;
								break;
							case QuestionnaireKey.VERIFO:
								questionnaire[0].blocks[11].fields[1].value = issuingCountry;
								break;
							default:
								break;
						}
					}
					this.setState(
						prevState => ({
							verification: {
								...(prevState.verification as Verification),
								issuingCountry,
								questionnaire
							}
						}),
						() => resolve()
					);
				});

			handleDocumentTypeSelect = (documentType: DocumentType) =>
				new Promise(resolve => {
					this.mediaFilesList = [];
					this.isNewMediaSet = true;
					this.uploadQueue.length = 0;
					this.increaseProgress();
					// Update data in questionnaire as questionaire wont be rendered for it to update its values
					const questionnaire = this.state.verification?.questionnaire;
					if (questionnaire) {
						switch (this.state?.client?.key) {
							case QuestionnaireKey.SPECTROCOIN_LOANS:
							case QuestionnaireKey.BANKERA_LOANS:
								questionnaire[0].blocks[7].fields[0].value = documentType;
								break;
							case QuestionnaireKey.VERIFO:
								questionnaire[0].blocks[11].fields[0].value = documentType;
								break;
							default:
								break;
						}
					}
					this.setState(
						prevState => ({
							isRequiredCaptureObjectListCreated: false,
							verification: {
								...(prevState.verification as Verification),
								documentType,
								questionnaire
							}
						}),
						() => {
							if (documentType !== DocumentType.DRIVERS_LICENCE) {
								return this.openCameraPage().then(() => {
									this.submitDocumentInformation().then(resolve);
								});
							}
							return resolve('');
						}
					);
				});

			handleCitizenshipSelect = (citizenship: string) =>
				new Promise(resolve => {
					this.increaseProgress();
					const questionnaire = this.state.verification?.questionnaire;
					if (questionnaire) {
						switch (this.state?.client?.key) {
							case QuestionnaireKey.PERVESK:
								questionnaire[0].blocks[3].fields[0].value = citizenship;
								break;
							case QuestionnaireKey.PERVESK_CORPORATE:
								questionnaire[1].blocks[1].fields[0].value = citizenship;
								break;
							case QuestionnaireKey.VERIFO:
							case QuestionnaireKey.BANKERA_LOANS:
							case QuestionnaireKey.SPECTROCOIN_LOANS:
								questionnaire[0].blocks[4].fields[0].value = citizenship;
								break;
							default:
								break;
						}
					}
					this.setState(
						prevState => ({
							verification: {
								...(prevState.verification as Verification),
								citizenship,
								questionnaire
							}
						}),
						() => {
							this.openCameraPage().then(() => {
								this.submitDocumentInformation().then(resolve);
							});
						}
					);
				});

			loadClientQuestionnaire = () => {
				let introComponentPromise: Promise<{
					default: React.ComponentType<IntroProps>;
				}>;
				let questionnaireComponentPromise: Promise<{
					default: React.ComponentType<QuestionnaireProps>;
				}>;
				let agreementComponentPromise: Promise<{
					default: React.ComponentType<AgreementProps>;
				}>;
				let secondAgreementComponentPromise: Promise<{
					default: React.ComponentType<AgreementProps>;
				}>;
				let mediaComponentPromise: Promise<{
					default: React.ComponentType<MediaProps & CameraProps>;
				}>;
				let permissionComponentPromise: Promise<{
					default: React.ComponentType<PermissionsProps>;
				}>;
				let verificationHandoverPromise: Promise<{
					default: React.ComponentType<VerificationHandoverProps>;
				}>;
				let questionnaireLength: number;
				let isAgreementEnabled = false;
				let isSecondAgreementEnabled = false;
				let isMediaBeforeQuestionnaire =
					this.state.verification?.stages.indexOf(Stage.MEDIA) === 0 ||
					(this.state.verification?.stages.indexOf(Stage.DOCUMENT_UPLOAD) === 0 &&
						this.state.verification.stages.indexOf(Stage.MEDIA) === 1);
				// move media to the end if prefill is enabled
				if (this.state.client?.isQuestionnairePrefillEnabled) {
					isMediaBeforeQuestionnaire = false;
				}
				let isProgressBarEnabled = true;
				let isSuccessTextShort = false;
				let privacyPolicyLink = '';
				let termsAndConditionsLink = '';
				let isResidencePermitAvailable = true;

				if (this.state.client) {
					mediaComponentPromise = import('../questionnaire/Media');
					introComponentPromise = import('../Intro/Intro');
					permissionComponentPromise = import('../Permissions/Permissions');
					verificationHandoverPromise = import(
						'../VerificationHandover/VerificationHandover'
					);
					switch (this.state.client.key) {
						case QuestionnaireKey.BANKERA_LOANS:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/bankeraLoans/Questionnaire'
							);
							questionnaireLength = 3;
							break;
						case QuestionnaireKey.PPB:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/PPB/Questionnaire'
							);
							questionnaireLength = 2;
							isResidencePermitAvailable = false;
							permissionComponentPromise = import('../Permissions/SinglePermission');
							privacyPolicyLink = 'https://pacificprivatebank.com/privacy-policy/';
							break;
						case QuestionnaireKey.PPB_KYB:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/clients/PPBKYB/Intro/Intro')
								: import('../questionnaire/clients/PPBKYB/Intro/IntroOtherTiers');
							questionnaireComponentPromise = import(
								'../questionnaire/clients/PPBKYB/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							privacyPolicyLink = 'https://pacificprivatebank.com/privacy-policy/';
							termsAndConditionsLink =
								'https://pacificprivatebank.com/ppb-general-termsconditions.pdf';
							break;
						case QuestionnaireKey.PPB_MEDIA:
							introComponentPromise = import(
								'../questionnaire/clients/PPBPrivate/Intro/Intro'
							);

							questionnaireComponentPromise = import(
								'../questionnaire/clients/PPBMedia/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 2;
							privacyPolicyLink = 'https://pacificprivatebank.com/privacy-policy/';
							termsAndConditionsLink =
								'https://pacificprivatebank.com/ppb-general-termsconditions.pdf';
							break;
						case QuestionnaireKey.PPB_PRIVATE:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/clients/PPBPrivate/Intro/Intro')
								: import(
										'../questionnaire/clients/PPBPrivate/Intro/IntroOtherTiers'
								  );
							questionnaireComponentPromise = import(
								'../questionnaire/clients/PPBPrivate/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							privacyPolicyLink = 'https://pacificprivatebank.com/privacy-policy/';
							termsAndConditionsLink =
								'https://pacificprivatebank.com/ppb-general-termsconditions.pdf';
							break;
						case QuestionnaireKey.VERIFO:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/verifo/Questionnaire'
							);
							agreementComponentPromise = import(
								'../questionnaire/clients/verifo/Agreement'
							);
							questionnaireLength = 6;
							isAgreementEnabled = true;
							break;
						case QuestionnaireKey.FINRAX_BUSINESS:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/clients/FinraxBusiness/Intro/Intro')
								: import(
										'../questionnaire/clients/FinraxBusiness/Intro/IntroOtherTiers'
								  );
							questionnaireComponentPromise = import(
								'../questionnaire/clients/FinraxBusiness/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.FINRAX_KYB_2023:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/clients/finraxKYB2023/Intro/Intro')
								: import(
										'../questionnaire/clients/finraxKYB2023/Intro/IntroOtherTiers'
								  );
							questionnaireComponentPromise = import(
								'../questionnaire/clients/finraxKYB2023/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.PERVESK:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/pervesk/Questionnaire'
							);
							agreementComponentPromise = import(
								'../questionnaire/clients/pervesk/PrivacyPolicy'
							);
							secondAgreementComponentPromise = import(
								'../questionnaire/clients/pervesk/TermsConditionsAndAccountAgreement'
							);
							questionnaireLength = 6;
							isAgreementEnabled = true;
							isSecondAgreementEnabled = true;
							break;
						case QuestionnaireKey.PERVESK_CORPORATE:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/perveskCorporate/Questionnaire'
							);
							questionnaireLength = 8;
							break;
						case QuestionnaireKey.CRYPTO_FOREST:
							introComponentPromise = import(
								'../questionnaire/clients/cryptoForest/Intro/Intro'
							);
							questionnaireComponentPromise = import(
								'../questionnaire/clients/cryptoForest/Questionnaire'
							);
							questionnaireLength = 3;
							break;
						case QuestionnaireKey.PERVESK_MEDIA:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/perveskMedia/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 2;
							privacyPolicyLink = 'https://pervesk.lt/en/privacy-policy/';
							break;
						case QuestionnaireKey.SPECTROCOIN_MEDIA:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/spectrocoinMedia/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 2;
							break;
						case QuestionnaireKey.SPECTROCOIN_IBAN:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/spectrocoinIBAN/Questionnaire'
							);
							permissionComponentPromise = import(
								'../Permissions/DoublePermissionsPVK'
							);
							questionnaireLength = 4;
							isAgreementEnabled = false;
							break;
						case QuestionnaireKey.SPECTROCOIN_IBAN_2023:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/spectrocoinIBAN2023/Questionnaire'
							);
							permissionComponentPromise = import(
								'../Permissions/DoublePermissionsPVK'
							);
							questionnaireLength = 4;
							isAgreementEnabled = false;
							break;
						case QuestionnaireKey.BANKERA_WALLET:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/IntroBNK/Intro')
								: import('../questionnaire/IntroBNK/IntroOtherTiers');
							questionnaireComponentPromise = import(
								'../questionnaire/clients/bankeraWallet/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							privacyPolicyLink = 'https://bankera.com/privacy-policy/';
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.BANKERA_KYB_2023:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/IntroBNK/Intro')
								: import('../questionnaire/IntroBNK/IntroOtherTiers');
							questionnaireComponentPromise = import(
								'../questionnaire/clients/bankeraKYB2023/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							privacyPolicyLink = 'https://bankera.com/privacy-policy/';
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.BANKERA_PRIVATE_2023:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/IntroBNK/Intro')
								: import('../questionnaire/IntroBNK/IntroOtherTiers');
							questionnaireComponentPromise = import(
								'../questionnaire/clients/bankeraPrivate2023/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							privacyPolicyLink = 'https://bankera.com/privacy-policy/';
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.PHOENIX_PRIVATE:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/clients/phoenixPrivate/Intro/Intro')
								: import(
										'../questionnaire/clients/phoenixPrivate/Intro/IntroOtherTiers'
								  );
							questionnaireComponentPromise = import(
								'../questionnaire/clients/phoenixPrivate/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.PHOENIX_CORPORATE:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/clients/phoenixCorporate/Intro/Intro')
								: import(
										'../questionnaire/clients/phoenixCorporate/Intro/IntroOtherTiers'
								  );
							questionnaireComponentPromise = import(
								'../questionnaire/clients/phoenixCorporate/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.PHOENIX_TIERS:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/clients/phoenixTiers/Intro/Intro')
								: import(
										'../questionnaire/clients/phoenixTiers/Intro/IntroOtherTiers'
								  );
							questionnaireComponentPromise = import(
								'../questionnaire/clients/phoenixTiers/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.NEOCARD_PRIVATE:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/clients/neoCardPrivate/Intro/Intro')
								: import(
										'../questionnaire/clients/neoCardPrivate/Intro/IntroOtherTiers'
								  );
							questionnaireComponentPromise = import(
								'../questionnaire/clients/neoCardPrivate/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.NEOCARD_CORPORATE:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/clients/neoCardCorporate/Intro/Intro')
								: import(
										'../questionnaire/clients/neoCardCorporate/Intro/IntroOtherTiers'
								  );
							questionnaireComponentPromise = import(
								'../questionnaire/clients/neoCardCorporate/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.NEOCARD_TIERS:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/clients/neoCardTiers/Intro/Intro')
								: import(
										'../questionnaire/clients/neoCardTiers/Intro/IntroOtherTiers'
								  );
							questionnaireComponentPromise = import(
								'../questionnaire/clients/neoCardTiers/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.COLUMIS:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/IntroBNK/Intro')
								: import('../questionnaire/IntroBNK/IntroOtherTiers');
							questionnaireComponentPromise = import(
								'../questionnaire/clients/columis/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.TEROXX:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/IntroBNK/Intro')
								: import('../questionnaire/IntroBNK/IntroOtherTiers');
							questionnaireComponentPromise = import(
								'../questionnaire/clients/teroxx/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.TEROXX_CORPORATE:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/IntroBNK/Intro')
								: import('../questionnaire/IntroBNK/IntroOtherTiers');
							questionnaireComponentPromise = import(
								'../questionnaire/clients/teroxxCorporate/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.TEROXX_MEDIA:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/IntroBNK/Intro')
								: import('../questionnaire/IntroBNK/IntroOtherTiers');
							questionnaireComponentPromise = import(
								'../questionnaire/clients/teroxxMedia/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 2;
							break;
						case QuestionnaireKey.DAW_CORPORATE:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/IntroBNK/Intro')
								: import('../questionnaire/IntroBNK/IntroOtherTiers');
							questionnaireComponentPromise = import(
								'../questionnaire/clients/dawCorporate/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.DAW_MEDIA:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/IntroBNK/Intro')
								: import('../questionnaire/IntroBNK/IntroOtherTiers');
							questionnaireComponentPromise = import(
								'../questionnaire/clients/dawMedia/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 2;
							break;
						case QuestionnaireKey.BENKER:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/IntroBNK/Intro')
								: import('../questionnaire/IntroBNK/IntroOtherTiers');
							questionnaireComponentPromise = import(
								'../questionnaire/clients/benker/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.BENKER_CORPORATE:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/IntroBNK/Intro')
								: import('../questionnaire/IntroBNK/IntroOtherTiers');
							questionnaireComponentPromise = import(
								'../questionnaire/clients/benkerCorporate/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.BENKER_MEDIA:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/IntroBNK/Intro')
								: import('../questionnaire/IntroBNK/IntroOtherTiers');
							questionnaireComponentPromise = import(
								'../questionnaire/clients/benkerMedia/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 2;
							break;
						case QuestionnaireKey.KVIX:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/clients/kvix/Intro/Intro')
								: import('../questionnaire/clients/kvix/Intro/IntroOtherTiers');
							questionnaireComponentPromise = import(
								'../questionnaire/clients/kvix/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 5;
							isProgressBarEnabled = false;
							this.isPermissionsPageEnabled = false;
							break;
						case QuestionnaireKey.BANKERA_MEDIA:
							introComponentPromise = this.state.verification?.stages.includes(
								Stage.MEDIA
							)
								? import('../questionnaire/clients/bankeraWallet/Intro/Intro')
								: import(
										'../questionnaire/clients/bankeraWallet/Intro/IntroOtherTiers'
								  );
							questionnaireComponentPromise = import(
								'../questionnaire/clients/bankeraMedia/Questionnaire'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							questionnaireLength = 2;
							privacyPolicyLink = 'https://bankera.com/privacy-policy/';
							break;
						case QuestionnaireKey.SPECTROCOIN_LOANS:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/spectrocoinLoans/Questionnaire'
							);
							questionnaireLength = 3;
							break;
						case QuestionnaireKey.SPECTROCOIN_CORPORATE:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/spectrocoinCorporate/Questionnaire'
							);
							questionnaireLength = 8;
							break;
						case QuestionnaireKey.SPECTROCOIN_CORPORATE_BVI:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/BVI/Questionnaire'
							);
							questionnaireLength = 8;
							break;
						case QuestionnaireKey.PAXFUL_LOCALBITCOINS:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/paxfulLocalBitcoins/Questionnaire'
							);
							permissionComponentPromise = import(
								'../Permissions/PrivacyPolicyPermission'
							);
							introComponentPromise = import(
								'../questionnaire/clients/paxfulLocalBitcoins/Intro/Intro'
							);
							isSuccessTextShort = true;
							privacyPolicyLink =
								'https://spectrocoin.com/assets/files/Spectro_Finance_Ltd_Privacy_Policy.pdf';
							questionnaireLength = 2;
							break;
						case QuestionnaireKey.BLACKRIDGE_CM_CORPORATE:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/blackridgeCorporate/Questionnaire'
							);
							introComponentPromise = import(
								'../questionnaire/clients/blackridgePrivate/Intro/Intro'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							privacyPolicyLink = 'https://www.blackridgecm.com/privacy-policy';
							questionnaireLength = 9;
							break;
						case QuestionnaireKey.BLACKRIDGE_CM_PRIVATE:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/blackridgePrivate/Questionnaire'
							);
							introComponentPromise = import(
								'../questionnaire/clients/blackridgePrivate/Intro/Intro'
							);
							permissionComponentPromise = import('../Permissions/SinglePermission');
							privacyPolicyLink = 'https://www.blackridgecm.com/privacy-policy';
							questionnaireLength = 5;
							break;
						case QuestionnaireKey.AMNERA_EOOD_PRIVATE:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/amneraPrivate/Questionnaire'
							);
							questionnaireLength = 2;
							break;
						case QuestionnaireKey.AMNERA_EOOD_CORPORATE:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/amneraCorporate/Questionnaire'
							);
							questionnaireLength = 6;
							break;
						case QuestionnaireKey.SPECTROCOIN_EE:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/spectrocoinEE/Questionnaire'
							);
							questionnaireLength = 4;
							break;
						case QuestionnaireKey.SPECTROCOIN_LT:
						default:
							questionnaireComponentPromise = import(
								'../questionnaire/clients/spectrocoinLT/Questionnaire'
							);
							questionnaireLength = 4;
							break;
					}
					this.setState({
						questionnaireLength,
						isAgreementEnabled,
						isSecondAgreementEnabled,
						isMediaBeforeQuestionnaire,
						isProgressBarEnabled,
						isSuccessTextShort,
						privacyPolicyLink,
						termsAndConditionsLink,
						isResidencePermitAvailable
					});

					this.introComponent = lazy(() => introComponentPromise);
					this.questionnaireComponent = lazy(() => questionnaireComponentPromise);
					this.agreementComponent = lazy(() => agreementComponentPromise);
					this.secondAgreementComponent = lazy(() => secondAgreementComponentPromise);
					this.mediaComponent = lazy(() => mediaComponentPromise);
					this.permissionComponent = lazy(() => permissionComponentPromise);
					this.verificationHandoverComponent = lazy(() => verificationHandoverPromise);
				}
			};

			setBoldDeskWidgetScript = () => {
				const { client } = this.state;

				if (!client?.key) {
					return;
				}

				const script = document.createElement('script');
				script.defer = true;
				script.async = true;
				script.src = getBoldDeskWidgetScriptSrc(client.key);

				document.head.appendChild(script);

				this.boldDeskScript = script;
			};

			isBoldDeskEnabled = () => {
				return (
					!!this.state.client &&
					[
						QuestionnaireKey.BANKERA_WALLET,
						QuestionnaireKey.BANKERA_KYB_2023,
						QuestionnaireKey.BANKERA_PRIVATE_2023,
						QuestionnaireKey.BANKERA_MEDIA,
						QuestionnaireKey.SPECTROCOIN_EE,
						QuestionnaireKey.SPECTROCOIN_CORPORATE,
						QuestionnaireKey.SPECTROCOIN_MEDIA,
						QuestionnaireKey.SPECTROCOIN_IBAN,
						QuestionnaireKey.SPECTROCOIN_IBAN_2023
					].includes(this.state.client.key) &&
					!isRunningInIFrame() &&
					!window?.handler?.onNativeCameraRequested &&
					!this.startFromApp &&
					!this.continueFromApp
				);
			};

			setLanguageList = (languageList: string[]) => {
				this.setState({
					languageList
				});
			};

			finishQuestionnaireUpdate = () => {
				if (!this.state.isQuestionnaireComplete) {
					this.setState({
						isQuestionnaireUpdating: false
					});
				}
			};

			render(): React.ReactNode {
				let page: ReactElement | null = null;
				const currentProgress = getProgressValue(
					this.isComingFromApp ? this.appTotalSteps : this.getTotalSteps(),
					this.state.progressBarPosition
				);
				const Agreement = this.agreementComponent;
				const QuestionnaireComponent = this.questionnaireComponent;
				const Media = this.mediaComponent;
				const IntroComponent = this.introComponent;
				const Permissions = this.permissionComponent;
				const VerificationHandover = this.verificationHandoverComponent;

				switch (this.state.activePage) {
					case Page.BLANK:
						break;
					case Page.ENTRY:
						page = (
							<Suspense fallback={<LoadingSpinner />}>
								<Entry
									hasCamera={this.state.hasCamera}
									fetchVerification={this.fetchVerification}
									openVerification={this.openVerification}
									openPage={this.openPage}
									isMediaOnly={this.isMediaOnly()}
									setLanguageList={this.setLanguageList}
									setInitialLanguage={this.setInitialLanguage}
									handlePageChange={this.handlePageChange}
								/>
							</Suspense>
						);
						break;
					case Page.INTRO:
						page = (
							<Suspense fallback={<LoadingSpinner />}>
								<IntroComponent
									companyName={
										this.state?.client && this.state.client.displayTitle
									}
									onNextClick={this.handleIntroNextClick}
									sessionKey={this.sessionKey}
									nextDisabled={
										this.state.isRequestingCameraPermission ||
										!this.state.gmapsLoaded
									}
									handlePageChange={this.handlePageChange}
									isResidencePermitAvailable={
										this.state.isResidencePermitAvailable
									}
								/>
							</Suspense>
						);
						break;
					case Page.PERMISSIONS:
						page = (
							<Suspense fallback={<LoadingSpinner />}>
								<Permissions
									onNextClick={this.handlePermissionsNextClick}
									progress={currentProgress}
									isQuestionnaireOnly={this.isQuestionnaireOnly()}
									logUserEvent={this.logUserEvent}
									questionnaireKey={this.state.client?.key}
									handlePageChange={this.handlePageChange}
									privacyPolicyLink={this.state.privacyPolicyLink}
									termsAndConditionsLink={this.state.termsAndConditionsLink}
								/>
							</Suspense>
						);
						break;
					case Page.AGREEMENT:
						page = (
							<Suspense fallback={<LoadingSpinner />}>
								<Agreement
									onNextClick={this.handleAgreementNextClick}
									progress={currentProgress}
									logUserEvent={this.logUserEvent}
									handlePageChange={this.handlePageChange}
								/>
							</Suspense>
						);
						break;
					case Page.MEDIA:
						if (this.state.verification && this.state.client) {
							page = (
								<Suspense fallback={<LoadingSpinner />}>
									<Media
										activeCaptureObject={this.state.activeCaptureObject}
										documentType={this.state.verification.documentType}
										onTakeSnapshot={this.takeSnapshot}
										onConfirm={this.handleConfirm}
										stream={this.mediaStream}
										saveImage={this.saveImage}
										mediaMode={this.mediaMode}
										cameraKey={this.state.activeCaptureObject}
										videoRef={this.videoRef}
										progress={currentProgress}
										logUserEvent={this.logUserEvent}
										handleCountrySelect={this.handleCountrySelect}
										handleDocumentTypeSelect={this.handleDocumentTypeSelect}
										handleCitizenshipSelect={this.handleCitizenshipSelect}
										country={this.state.verification.issuingCountry}
										citizenship={this.state.verification.citizenship}
										disabledCountries={this.state.client.disabledCountries}
										openCameraPage={this.openCameraPage}
										handlePageChange={this.handlePageChange}
										decreaseProgress={this.decreaseProgress}
										callNativeMobileCamera={this.callNativeMobileCamera}
										client={this.state.client.key}
										brightnessThreshold={{
											min: this.state.client.tooDarkThreshold,
											max: this.state.client.tooBrightThreshold
										}}
										blurThreshold={this.state.client.blurLevel}
										restartCamera={this.restartCamera}
										startRecording={this.startRecording}
										stopMedia={this.stopMedia}
										startMedia={this.startMedia}
										allowedDocuments={this.state.client.allowedDocuments}
										isRetryingQuestionnaire={this.isRetryingQuestionnaire()}
										publicNotes={this.state.verification.publicNotes}
										sessionKey={this.sessionKey}
										handoverUrl={
											this.state.client ? this.state.client.handoverUrl : null
										}
										isBNKOrPVKCorporate={this.state.isBNKOrPVKCorporate}
										startFromApp={this.startFromApp}
									/>
								</Suspense>
							);
						}
						break;

					case Page.LOADING:
						page = (
							<Loading
								progress={currentProgress}
								handlePageChange={this.handlePageChange}
							/>
						);
						break;

					case Page.SUCCESS:
						page = (
							<Suspense fallback={<LoadingSpinner />}>
								<Success
									onDoneClick={this.handleDoneClick}
									companyName={
										this.state.client
											? !isValueEmpty(this.state.client.displayTitle)
												? this.state.client.displayTitle
												: ''
											: ''
									}
									handlePageChange={this.handlePageChange}
									isKYCApp={this.isKYCApp}
									isTextShort={this.state.isSuccessTextShort}
									shouldShowHandoverUrl={this.state.shouldShowHandoverUrl}
								/>
							</Suspense>
						);
						break;

					case Page.UNAVAILABLE:
						page = (
							<Suspense fallback={<LoadingSpinner />}>
								<Unavailable
									onNextClick={this.handleUnavailableNextClick}
									handlePageChange={this.handlePageChange}
								/>
							</Suspense>
						);
						break;

					case Page.QR:
						this.logUserEvent(UserEvent.QR_CODE_SHOWN);
						// eslint-disable-next-line react/jsx-pascal-case
						page = (
							<Suspense fallback={<LoadingSpinner />}>
								<QR
									sessionKey={this.sessionKey}
									logUserEvent={this.logUserEvent}
									handoverUrl={
										this.state.client ? this.state.client.handoverUrl : null
									}
									titleMessage={
										this.state.isCameraResolutionTooLow
											? messages.qrTitleLowCam
											: messages.qrTitleNoCam
									}
									clientKey={this.state.client?.key}
									isBNKOrPVKCorporate={this.state.isBNKOrPVKCorporate}
									handlePageChange={this.handlePageChange}
								/>
							</Suspense>
						);
						break;

					case Page.INTERIM_QR:
						page = (
							<Suspense fallback={<LoadingSpinner />}>
								<InterimQR
									sessionKey={this.sessionKey}
									onNextClick={this.handleInterimQRNextClick}
									logUserEvent={this.logUserEvent}
									handoverUrl={
										this.state.client ? this.state.client.handoverUrl : null
									}
									clientKey={this.state.client?.key}
									isBNKOrPVKCorporate={this.state.isBNKOrPVKCorporate}
									handlePageChange={this.handlePageChange}
								/>
							</Suspense>
						);
						break;

					case Page.QR_READER:
						page = (
							<Suspense fallback={<LoadingSpinner />}>
								<QRReader
									openVerification={this.openVerification}
									openPage={this.openPage}
									fetchVerification={this.fetchVerification}
								/>
							</Suspense>
						);
						break;
					case Page.DOCUMENT_UPLOAD:
						if (this.state.verification && this.state.client) {
							page = (
								<Suspense fallback={<LoadingSpinner />}>
									<DocumentUpload
										sessionKey={this.sessionKey}
										requiredFiles={this.state.verification.supportingDocuments}
										onNextClick={this.handleDocumentUploadNextClick}
										agentComment={this.state.verification.document?.comment}
										parentLogUserEvent={this.logUserEvent}
										progress={currentProgress}
									/>
								</Suspense>
							);
						}
						break;
					case Page.ADDITIONAL_QUESTIONS:
						if (this.state.verification && this.state.client) {
							page = (
								<Suspense fallback={<LoadingSpinner />}>
									<AdditionalQuestions
										sessionToken={this.sessionKey}
										onNextClick={this.handleAdditionalQuestionsNextClick}
										isQuestionnairePrefillEnabled={
											this.state.client.isQuestionnairePrefillEnabled
										}
										publicNotes={this.state.verification.publicNotes}
										fail={this.fail}
									/>
								</Suspense>
							);
						}
						break;

					case Page.QUESTIONNAIRE:
						if (
							this.state.verification &&
							this.state.client &&
							this.state.gmapsLoaded
						) {
							page = (
								<Suspense fallback={<LoadingSpinner />}>
									<QuestionnaireComponent
										clientKey={this.state.client.key}
										logUserEvent={this.logUserEvent}
										increaseProgress={this.increaseProgress}
										decreaseProgress={this.decreaseProgress}
										progressBarPosition={this.state.progressBarPosition}
										progress={currentProgress}
										onSubmit={this.handleQuestionnaireSubmit}
										questionnaire={this.state.verification.questionnaire}
										stages={this.state.verification.stages}
										questionnaireFailures={
											this.state.verification.questionnaireFailures
										}
										isQuestionnaireComplete={this.state.isQuestionnaireComplete}
										isQuestionnaireOnly={this.isQuestionnaireOnly()}
										isMediaBeforeQuestionnaire={
											this.state.isMediaBeforeQuestionnaire
										}
										isRetryingQuestionnaire={this.isRetryingQuestionnaire()}
										isNameEditable={this.state.isNameEditable}
										isEmailEditable={this.state.isEmailEditable}
										isPhoneNumberEditable={this.state.isPhoneNumberEditable}
										isDocumentEditable={this.state.isDocumentEditable}
										minVerifiedAge={this.state.client.minVerifiedAge}
										maxVerifiedAge={this.state.client.maxVerifiedAge}
										disabledCountries={this.state.client.disabledCountries}
										handlePageChange={this.handlePageChange}
										sessionKey={this.sessionKey}
										information={{
											...this.getDocumentInformation(),
											...this.getContactInformation()
										}}
										isCompanyRepresentative={
											this.state.verification.isCompanyRepresentative
										}
										isLegalEntity={
											this.state.verification.isLegalEntity ||
											this.state.client.key.includes('CORPORATE') ||
											this.state.client.key === QuestionnaireKey.PPB_KYB ||
											this.state.client.key ===
												QuestionnaireKey.BANKERA_KYB_2023 ||
											this.state.client.key ===
												QuestionnaireKey.FINRAX_KYB_2023
										}
										requiredFiles={this.state.verification.supportingDocuments}
										failedBlockValues={this.state.failedBlockValues}
										allowedDocuments={this.state.client.allowedDocuments}
										completedQuestionnaireStages={
											this.state.completedQuestionnaireStages
										}
										isPrefillEnabled={
											this.state.client.isQuestionnairePrefillEnabled
										}
										issuingCountry={this.state.verification.issuingCountry}
										publicNotes={this.state.verification.publicNotes}
										corporateVerifications={this.state.corporateVerifications}
										finishQuestionnaireUpdate={this.finishQuestionnaireUpdate}
									/>
								</Suspense>
							);
						}
						break;

					case Page.VERIFICATION_HANDOVER:
						if (this.state.verification && this.state.client) {
							page = (
								<Suspense fallback={<LoadingSpinner />}>
									<VerificationHandover
										progress={currentProgress}
										sessionKey={this.sessionKey}
										isPPBKYB={
											this.state.client.key === QuestionnaireKey.PPB_KYB
										}
										logUserEvent={this.logUserEvent}
										handleIWillPassToOtherPersonCLick={
											this.handlePrefillForward
										}
										onNextClick={this.handlePrefillNextClick}
									/>
								</Suspense>
							);
						}
						break;

					case Page.CONFIRMATION:
						if (this.state.verification && this.state.client) {
							page = (
								<Suspense fallback={<LoadingSpinner />}>
									<Confirmation
										clientKey={this.state.client.key}
										isUBOConfirmationEnabled={
											this.state.client.key !== QuestionnaireKey.PPB_KYB
										}
										isMediaBeforeQuestionnaire={
											this.state.isMediaBeforeQuestionnaire
										}
										isMedia={this.isMedia()}
										isBNKOrPVKCorporate={this.state.isBNKOrPVKCorporate}
										handleNextClick={this.handleConfirmationNextClick}
									/>
								</Suspense>
							);
						}
						break;

					case Page.APPLICATION_PAYMENT:
						if (this.state.paymentInfo) {
							page = (
								<Suspense fallback={<LoadingSpinner />}>
									<ApplicationPayment
										sessionKey={this.sessionKey}
										paymentInfo={this.state.paymentInfo}
										setPaymentInfo={this.setPaymentInfo}
										logUserEvent={this.logUserEvent}
									/>
								</Suspense>
							);
						}
						break;

					case Page.UNDER_REVIEW:
						page = (
							<Suspense fallback={<LoadingSpinner />}>
								<UnderReview
									sessionKey={this.sessionKey}
									viewState={this.state.viewState}
									logUserEvent={this.logUserEvent}
								/>
							</Suspense>
						);
						break;

					case Page.FAILURE:
					default:
						page = (
							<Suspense fallback={<LoadingSpinner />}>
								<Failure
									failureReason={this.state.failureReason || undefined}
									companyName={
										this.state.client
											? !isValueEmpty(this.state.client.displayTitle)
												? this.state.client.displayTitle
												: ''
											: undefined
									}
									onFailClick={
										this.state.client ? this.handleFailClick : undefined
									}
									handlePageChange={this.handlePageChange}
								/>
							</Suspense>
						);
						break;
				}

				if (
					!this.locale &&
					this.state.activePage !== Page.FAILURE &&
					this.state.activePage !== Page.UNDER_REVIEW &&
					this.state.activePage !== Page.APPLICATION_PAYMENT
				) {
					return null;
				}

				return (
					<IntlProvider
						locale={this.state.language || 'en'}
						messages={this.state.languageData}
					>
						<ThemeContext.Provider value={this.themeFile}>
							<CompanyTitleContext.Provider
								value={
									this.state.client
										? !isValueEmpty(this.state.client.displayTitle)
											? this.state.client.displayTitle
											: ''
										: ''
								}
							>
								<ProgressBarContext.Provider
									value={this.state.isProgressBarEnabled}
								>
									<LanguageComponent
										handleLanguageSelect={this.handleLanguageChange}
										currentLanguage={this.locale || 'en'}
										allowedLanguages={
											this.state.client?.allowedLanguages ||
											this.state.languageList
										}
									/>
									<QuestionnaireUpdateContext.Provider
										value={this.state.isQuestionnaireUpdating}
									>
										<DocumentIssuingCountryContext.Provider
											value={
												this.state.verification &&
												this.state.verification.issuingCountry
											}
										>
											{!isRunningInIFrame() &&
												this.sessionKey &&
												this.state.client && (
													<img
														src={`${config.apiBaseUrl}/clients/${this.sessionKey}/logo`}
														className={
															this.state.client.key ===
																QuestionnaireKey.PPB_KYB ||
															this.state.client.key ===
																QuestionnaireKey.PPB_PRIVATE ||
															this.state.client.key ===
																QuestionnaireKey.PPB_MEDIA
																? style.biggerSquareLogo
																: style.logo
														}
														alt=""
													/>
												)}
											{page}
										</DocumentIssuingCountryContext.Provider>
									</QuestionnaireUpdateContext.Provider>
								</ProgressBarContext.Provider>
							</CompanyTitleContext.Provider>
						</ThemeContext.Provider>
					</IntlProvider>
				);
			}
		}
	)
);
